<template>
  <div class="value-page">
    <section class="resource-toolbar">
      <div>
        <h2>{{ activeModule.title }}</h2>
        <p>{{ activeModule.description }}</p>
      </div>
      <div class="toolbar-actions">
        <v-btn v-if="activeKey === 'reports'" outlined color="primary" :loading="saving" @click="generateReport">
          <v-icon left size="18">mdi-file-chart-outline</v-icon>
          Gerar relatório
        </v-btn>
        <v-btn
          v-if="canCreateActiveModule"
          small
          depressed
          color="primary"
          class="toolbar-create-button"
          @click="openDialog()"
        >
          <v-icon left size="18">mdi-plus</v-icon>
          Adicionar
        </v-btn>
      </div>
    </section>

    <ListFilters
      v-model="filterDraft"
      :fields="filterFields"
      :loading="loading"
      @filter="applyFilters"
      @clear="applyFilters"
    />

    <section class="mobile-card-list">
      <div v-if="loading" class="mobile-list-state">
        <v-progress-circular indeterminate color="primary" size="24" />
        <span>Carregando registros...</span>
      </div>
      <div v-else-if="!filteredItems.length" class="mobile-list-state">
        <v-icon color="primary" size="30">{{ activeModule.icon }}</v-icon>
        <strong>Nenhum registro por enquanto</strong>
        <span>{{ emptyStateDescription }}</span>
      </div>
      <article v-for="item in filteredItems" v-else :key="item.id || mobileCardTitle(item)" class="mobile-data-card">
        <div class="mobile-data-card-head">
          <div>
            <strong>{{ mobileCardTitle(item) }}</strong>
            <span v-if="mobileCardSubtitle(item)">{{ mobileCardSubtitle(item) }}</span>
          </div>
          <v-chip v-if="mobileCardStatus(item)" small :color="mobileStatusColor(item)" :text-color="mobileStatusTextColor(item)">
            {{ mobileCardStatus(item) }}
          </v-chip>
        </div>

        <div v-if="mobileCardFields(item).length" class="mobile-data-card-body">
          <div v-for="field in mobileCardFields(item)" :key="field.value" class="mobile-data-field">
            <span>{{ field.text }}</span>
            <strong>{{ field.display }}</strong>
          </div>
        </div>

        <div class="mobile-data-card-actions">
          <v-btn v-if="activeKey === 'demands'" small outlined color="primary" @click="supportDemand(item)">
            Apoiar
          </v-btn>
          <v-btn v-if="activeKey === 'polls'" small outlined color="primary" @click="openVoteDialog(item)">
            Votar
          </v-btn>
          <v-btn v-if="activeKey === 'reports'" icon small title="Baixar PDF" @click.stop="downloadReportPdf(item)">
            <v-icon size="19">mdi-file-pdf-box</v-icon>
          </v-btn>
          <v-btn
            v-if="activeKey === 'units'"
            icon
            small
            :loading="loadingUnitAnalytics && selectedUnitAnalyticsId === item.id"
            title="Ver analítico"
            @click.stop="openUnitAnalytics(item)"
          >
            <v-icon size="19">mdi-chart-box-outline</v-icon>
          </v-btn>
          <v-btn v-if="canEditActiveModule" icon small :loading="editingId === item.id" @click="openDialog(item)">
            <v-icon size="19">mdi-pencil-outline</v-icon>
          </v-btn>
          <v-btn
            v-if="canDeleteActiveModule"
            icon
            small
            color="error"
            :loading="deletingId === item.id"
            :title="deleteActionTitle"
            @click.stop="deleteItem(item)"
          >
            <v-icon size="19">mdi-delete-outline</v-icon>
          </v-btn>
        </div>
      </article>
    </section>

    <section class="table-panel">
      <BoaruaDataTable
        :headers="activeModule.headers"
        :items="filteredItems"
        :loading="loading"
        :empty-icon="activeModule.icon"
        :empty-description="emptyStateDescription"
      >
        <template v-slot:item.responsible_display="{ item }">
          <div class="responsible-cell">
            <strong>{{ unitResponsibleName(item) || "-" }}</strong>
            <span v-if="unitResponsibleDocumentDisplay(item)">{{ unitResponsibleDocumentDisplay(item) }}</span>
          </div>
        </template>
        <template v-slot:item.address_display="{ item }">
          {{ unitAddressTableDisplay(item) }}
        </template>
        <template v-slot:item.title="{ item }">
          {{ displayTitle(item) }}
        </template>
        <template v-slot:item.payment_status="{ item }">
          <v-chip small :color="paymentStatusColor(item)" :text-color="paymentStatusTextColor(item)">
            {{ paymentStatusLabel(item) }}
          </v-chip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip small color="secondary" text-color="primary">{{ optionLabel(item.status) }}</v-chip>
        </template>
        <template v-slot:item.type="{ item }">
          {{ optionLabel(item.type) }}
        </template>
        <template v-slot:item.address_type="{ item }">
          {{ optionLabel(item.address_type) }}
        </template>
        <template v-slot:item.post_type="{ item }">
          {{ optionLabel(item.post_type) }}
        </template>
        <template v-slot:item.execution_status="{ item }">
          {{ optionLabel(item.execution_status) }}
        </template>
        <template v-slot:item.priority="{ item }">
          {{ optionLabel(item.priority) }}
        </template>
        <template v-slot:item.waste_type="{ item }">
          {{ optionLabel(item.waste_type) }}
        </template>
        <template v-slot:item.weekday="{ item }">
          {{ optionLabel(item.weekday) }}
        </template>
        <template v-slot:item.schedule_json="{ item }">
          {{ scheduleSummary(item) }}
        </template>
        <template v-slot:item.category="{ item }">
          {{ optionLabel(item.category) }}
        </template>
        <template v-slot:item.audience="{ item }">
          {{ optionLabel(item.audience) }}
        </template>
        <template v-slot:item.is_featured="{ item }">
          {{ item.is_featured ? "Sim" : "Não" }}
        </template>
        <template v-slot:item.amount="{ item }">
          {{ item.amount ? money(item.amount) : "-" }}
        </template>
        <template v-slot:item.recurrence_frequency="{ item }">
          {{ optionLabel(item.recurrence_frequency || "none") }}
        </template>
        <template v-slot:item.next_due_date="{ item }">
          {{ formatDate(item.next_due_date) }}
        </template>
        <template v-slot:item.payment_receipt="{ item }">
          <v-btn v-if="receiptUrl(item)" icon small :href="receiptUrl(item)" target="_blank" title="Abrir comprovante">
            <v-icon size="19">mdi-paperclip</v-icon>
          </v-btn>
          <span v-else>-</span>
        </template>
        <template v-slot:item.support_count="{ item }">
          <strong>{{ item.support_count || 0 }}</strong>
        </template>
        <template v-slot:item.metrics_json="{ item }">
          {{ reportSummary(item) }}
        </template>
        <template v-slot:item.reference_month="{ item }">
          {{ formatReferenceMonth(item.reference_month) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn v-if="activeKey === 'demands'" small outlined color="primary" @click="supportDemand(item)">
            Apoiar
          </v-btn>
          <v-btn v-if="activeKey === 'polls'" small outlined color="primary" @click="openVoteDialog(item)">
            Votar
          </v-btn>
          <v-btn v-if="activeKey === 'reports'" icon small title="Baixar PDF" @click.stop="downloadReportPdf(item)">
            <v-icon size="19">mdi-file-pdf-box</v-icon>
          </v-btn>
          <v-btn
            v-if="activeKey === 'units'"
            icon
            small
            :loading="loadingUnitAnalytics && selectedUnitAnalyticsId === item.id"
            title="Ver analítico"
            @click.stop="openUnitAnalytics(item)"
          >
            <v-icon size="19">mdi-chart-box-outline</v-icon>
          </v-btn>
          <v-btn v-if="canEditActiveModule" icon small :loading="editingId === item.id" @click="openDialog(item)">
            <v-icon size="19">mdi-pencil-outline</v-icon>
          </v-btn>
          <v-btn
            v-if="canDeleteActiveModule"
            icon
            small
            color="error"
            :loading="deletingId === item.id"
            :title="deleteActionTitle"
            @click.stop="deleteItem(item)"
          >
            <v-icon size="19">mdi-delete-outline</v-icon>
          </v-btn>
        </template>
        <template v-slot:footer.prepend>
          <v-btn icon small title="Atualizar" @click="load">
            <v-icon size="19">mdi-refresh</v-icon>
          </v-btn>
        </template>
      </BoaruaDataTable>
    </section>

    <v-dialog v-model="deleteDialog" max-width="460" persistent>
      <v-card class="delete-dialog">
        <v-card-title>
          <v-icon color="error" size="26">mdi-alert-circle-outline</v-icon>
          <div>
            <strong>{{ deleteDialogTitle }}</strong>
            <span>Confirme antes de remover este registro.</span>
          </div>
        </v-card-title>
        <v-card-text>
          {{ deleteConfirmMessage(deleteItemLabel(itemToDelete || {})) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="!!deletingId" @click="closeDeleteDialog">Cancelar</v-btn>
          <v-btn depressed color="error" :loading="!!deletingId" @click="confirmDeleteItem">Excluir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" :max-width="dialogMaxWidth">
      <v-card class="form-dialog">
        <v-card-title>
          <div>
            <strong>{{ form.id ? "Editar" : "Novo" }} {{ activeModule.singular }}</strong>
            <span>{{ activeModule.description }}</span>
          </div>
        </v-card-title>
        <v-card-text class="form-dialog-body">
          <div v-if="activeKey === 'units'" class="business-step-dialog unit-step-dialog">
            <div class="business-step-heading">
              <v-icon size="18">{{ currentUnitStep.icon }}</v-icon>
              <div>
                <strong>{{ currentUnitStep.label }}</strong>
                <span>{{ currentUnitStep.description }}</span>
              </div>
            </div>

            <div v-if="unitStep === 'basic'" class="business-step-panel">
              <v-row dense class="business-dialog-fields">
                <v-col v-for="field in unitPrimaryFields" :key="field.name" cols="12" :md="field.cols || 6">
                  <v-select
                    v-if="field.type === 'select'"
                    v-model="form[field.name]"
                    :items="selectItems(field)"
                    :label="field.label"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="fieldErrors(field.name)"
                    @change="clearValidationError(field.name)"
                  />
                  <v-text-field
                    v-else
                    v-model="form[field.name]"
                    :label="field.label"
                    :type="field.type || 'text'"
                    v-mask="field.mask || ''"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="fieldErrors(field.name)"
                    @input="handleMaskedFieldInput(field)"
                  />
                </v-col>
              </v-row>
            </div>

            <div v-else-if="unitStep === 'address'" class="business-step-panel">
              <v-row dense>
                <v-col v-for="field in unitAddressFields" :key="field.name" cols="12" :md="field.cols || 6">
                  <v-autocomplete
                    v-if="field.type === 'address'"
                    v-model="selectedAddressSuggestion"
                    :items="addressOptions"
                    :search-input.sync="addressSearch"
                    :loading="searchingAddress"
                    item-text="display_name"
                    label="Endereço"
                    outlined
                    dense
                    hide-details="auto"
                    no-filter
                    clearable
                    return-object
                    :error-messages="fieldErrors(field.name)"
                    @change="onUnitAddressChange(field.name, $event)"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-icon>
                        <v-icon size="18">mdi-map-marker-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ item.display_name }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                  <v-text-field
                    v-else
                    v-model="form[field.name]"
                    :label="field.label"
                    :type="field.type || 'text'"
                    v-mask="field.mask || ''"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="fieldErrors(field.name)"
                    @input="handleMaskedFieldInput(field)"
                  />
                </v-col>
                <v-col cols="12">
                  <div class="point-map-picker unit-address-map">
                    <div class="point-map-toolbar">
                      <div>
                        <strong>Localização no mapa</strong>
                        <span>Use o autocomplete, localize pelo endereço ou arraste o pin para ajustar a residência.</span>
                      </div>
                      <v-btn small outlined color="primary" :loading="searchingPointAddress" @click="locatePointAddress">
                        <v-icon left size="16">mdi-map-search-outline</v-icon>
                        Localizar endereço
                      </v-btn>
                    </div>
                    <div ref="pointMap" class="point-map"></div>
                    <div class="point-map-footer">
                      <span v-if="form.latitude && form.longitude">{{ addressLocationMessage }}</span>
                      <span v-else>Selecione um endereço ou clique no mapa para definir o pin.</span>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <div v-else class="business-step-panel">
              <v-row dense>
                <v-col v-for="field in unitResponsibleFields" :key="field.name" cols="12" :md="field.cols || 6">
                  <div v-if="field.type === 'section'" class="form-section-heading">
                    <strong>{{ field.label }}</strong>
                    <span v-if="field.text">{{ field.text }}</span>
                  </div>
                  <div v-else-if="field.type === 'alert'" class="form-inline-alert">
                    <v-icon color="#057a3d" size="18">mdi-information-outline</v-icon>
                    <span>{{ field.text }}</span>
                  </div>
                  <v-text-field
                    v-else
                    v-model="form[field.name]"
                    :label="field.label"
                    :type="field.type || 'text'"
                    v-mask="field.mask || ''"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="fieldErrors(field.name)"
                    @input="handleMaskedFieldInput(field)"
                  />
                </v-col>
              </v-row>
            </div>
          </div>
          <div v-else-if="isBusinessDialog" class="business-step-dialog">
            <div class="business-step-heading">
              <v-icon size="18">{{ currentBusinessStep.icon }}</v-icon>
              <div>
                <strong>{{ currentBusinessStep.label }}</strong>
                <span>{{ currentBusinessStep.description }}</span>
              </div>
            </div>

            <div v-if="businessStep === 'basic'" class="business-step-panel">
              <v-row dense class="business-dialog-fields">
                <v-col v-for="field in businessBasicFields" :key="field.name" cols="12" :md="field.cols || 6">
                  <v-select
                    v-if="field.type === 'select'"
                    v-model="form[field.name]"
                    :items="selectItems(field)"
                    :label="field.label"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="fieldErrors(field.name)"
                    @change="clearValidationError(field.name)"
                  />
                  <v-textarea
                    v-else-if="field.type === 'textarea'"
                    v-model="form[field.name]"
                    :label="field.label"
                    outlined
                    rows="4"
                    hide-details="auto"
                    :error-messages="fieldErrors(field.name)"
                    @input="clearValidationError(field.name)"
                  />
                  <v-text-field
                    v-else
                    v-model="form[field.name]"
                    :label="field.label"
                    :type="field.type || 'text'"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="fieldErrors(field.name)"
                    @input="clearValidationError(field.name)"
                  />
                </v-col>
              </v-row>
            </div>

            <div v-else-if="businessStep === 'address'" class="business-step-panel">
              <div class="point-step-heading">
                <strong>Endereço e localização</strong>
                <span>Digite o endereço, clique em localizar e ajuste o pin se necessário.</span>
              </div>
              <v-row dense class="business-dialog-fields">
                <v-col cols="12">
                  <v-autocomplete
                    v-model="selectedAddressSuggestion"
                    :items="addressOptions"
                    :search-input.sync="addressSearch"
                    :loading="searchingAddress"
                    item-text="display_name"
                    label="Endereço"
                    outlined
                    dense
                    hide-details="auto"
                    no-filter
                    clearable
                    return-object
                    @change="applyAddressSuggestion"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-icon>
                        <v-icon size="18">mdi-map-marker-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ item.display_name }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12">
                  <div class="point-map-picker business-map-picker">
                    <div class="point-map-toolbar">
                      <div>
                        <strong>Pin do comércio</strong>
                        <span>Clique no mapa ou use o botão para localizar pelo endereço.</span>
                      </div>
                      <v-btn small outlined color="primary" :loading="searchingPointAddress" @click="locatePointAddress">
                        <v-icon left size="16">mdi-map-search-outline</v-icon>
                        Localizar
                      </v-btn>
                    </div>
                    <div ref="pointMap" class="point-map business-map"></div>
                    <div class="point-map-footer">
                      <span v-if="form.latitude && form.longitude">{{ addressLocationMessage }}</span>
                      <span v-else>Informe um endereço ou clique no mapa para definir o pin.</span>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <div v-else class="business-step-panel">
              <v-row dense class="business-dialog-fields">
                <v-col v-for="field in businessAdditionalFields" :key="field.name" cols="12" :md="field.cols || 6">
                  <v-select
                    v-if="field.type === 'select'"
                    v-model="form[field.name]"
                    :items="selectItems(field)"
                    :label="field.label"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="fieldErrors(field.name)"
                    @change="clearValidationError(field.name)"
                  />
                  <v-text-field
                    v-else
                    v-model="form[field.name]"
                    :label="field.label"
                    :type="field.type || 'text'"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="fieldErrors(field.name)"
                    @input="clearValidationError(field.name)"
                  />
                </v-col>
              </v-row>
            </div>
          </div>
          <v-row
            v-else-if="activeKey !== 'points' || pointStep === 'form'"
            dense
          >
            <v-col v-for="field in visibleFields" :key="field.name" cols="12" :md="field.cols || 6">
              <div v-if="field.type === 'section'" class="form-section-heading">
                <strong>{{ field.label }}</strong>
                <span v-if="field.text">{{ field.text }}</span>
              </div>
              <div v-else-if="field.type === 'alert'" class="form-inline-alert">
                <v-icon color="#057a3d" size="18">mdi-information-outline</v-icon>
                <span>{{ field.text }}</span>
              </div>
              <v-select
                v-else-if="field.type === 'select'"
                v-model="form[field.name]"
                :items="selectItems(field)"
                :label="field.label"
                outlined
                dense
                hide-details="auto"
                :error-messages="fieldErrors(field.name)"
                @change="clearValidationError(field.name)"
              />
              <v-autocomplete
                v-else-if="field.type === 'address'"
                v-model="form[field.name]"
                :items="addressOptions"
                :search-input.sync="addressSearch"
                :loading="searchingAddress"
                item-text="display_name"
                label="Endereço"
                outlined
                dense
                hide-details="auto"
                no-filter
                clearable
                return-object
                @change="applyAddressSuggestion"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-icon>
                    <v-icon size="18">mdi-map-marker-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.display_name }}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
              <v-textarea
                v-else-if="field.type === 'textarea'"
                v-model="form[field.name]"
                :label="field.label"
                outlined
                rows="3"
                hide-details="auto"
                :error-messages="fieldErrors(field.name)"
                @input="clearValidationError(field.name)"
              />
              <v-currency-field
                v-else-if="field.type === 'currency'"
                v-model="form[field.name]"
                :label="field.label"
                outlined
                dense
                hide-details="auto"
                :allow-negative="field.allowNegative !== false"
                :error-messages="fieldErrors(field.name)"
                @input="clearValidationError(field.name)"
              />
              <div
                v-else-if="field.type === 'file'"
                class="receipt-dropzone"
                :class="{ dragging: draggingFileField === field.name, filled: form[field.name] }"
                @dragenter.prevent="draggingFileField = field.name"
                @dragover.prevent="draggingFileField = field.name"
                @dragleave.prevent="draggingFileField = null"
                @drop.prevent="handleFileDrop(field.name, $event)"
              >
                <v-file-input
                  v-model="form[field.name]"
                  :label="field.label"
                  outlined
                  dense
                  show-size
                  prepend-icon=""
                  prepend-inner-icon="mdi-paperclip"
                  accept="image/*,.pdf"
                  hide-details="auto"
                />
                <div class="dropzone-hint">
                  <v-icon size="18">mdi-tray-arrow-down</v-icon>
                  <span>{{ form[field.name] ? fileName(form[field.name]) : "Arraste o comprovante para anexar" }}</span>
                </div>
              </div>
              <div v-else-if="field.type === 'schedule-list'" class="schedule-list-field">
                <div class="schedule-list-heading">
                  <span>{{ field.label }}</span>
                  <v-btn small text color="primary" @click="addScheduleItem">
                    <v-icon left size="16">mdi-plus</v-icon>
                    Adicionar dia
                  </v-btn>
                </div>
                <v-row
                  v-for="(schedule, index) in wasteScheduleRows"
                  :key="`schedule-${index}`"
                  dense
                  class="schedule-row"
                >
                  <v-col cols="12" md="5">
                    <v-select
                      v-model="schedule.weekday"
                      :items="weekdayOptions"
                      label="Dia"
                      outlined
                      dense
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col cols="6" md="3">
                    <v-text-field
                      v-model="schedule.starts_at"
                      label="Início"
                      type="time"
                      outlined
                      dense
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col cols="6" md="3">
                    <v-text-field
                      v-model="schedule.ends_at"
                      label="Fim"
                      type="time"
                      outlined
                      dense
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col cols="12" md="1" class="schedule-remove">
                    <v-btn icon small :disabled="wasteScheduleRows.length === 1" @click="removeScheduleItem(index)">
                      <v-icon size="18">mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
              <v-text-field
                v-else
                v-model="form[field.name]"
                :label="field.label"
                :type="field.type || 'text'"
                v-mask="field.mask || ''"
                outlined
                dense
                hide-details="auto"
                :error-messages="fieldErrors(field.name)"
                @input="clearValidationError(field.name)"
              />
            </v-col>
          </v-row>
          <div v-else class="point-location-step">
            <div class="point-step-heading">
              <strong>Endereço e localização</strong>
              <span>Busque o endereço, confirme o texto e arraste o pin para o ponto exato.</span>
            </div>
            <v-row dense>
              <v-col cols="12">
                <v-autocomplete
                  v-model="selectedAddressSuggestion"
                  :items="addressOptions"
                  :search-input.sync="addressSearch"
                  :loading="searchingAddress"
                  item-text="display_name"
                  label="Buscar endereço"
                  outlined
                  dense
                  hide-details="auto"
                  no-filter
                  clearable
                  return-object
                  @change="applyAddressSuggestion"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-icon>
                      <v-icon size="18">mdi-map-marker-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.display_name }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="form.address"
                  label="Endereço confirmado"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12">
              <div class="point-map-picker">
                <div class="point-map-toolbar">
                  <div>
                    <strong>Localização no mapa</strong>
                    <span>Clique no mapa ou arraste o pin para posicionar o ponto.</span>
                  </div>
                  <v-btn small outlined color="primary" :loading="searchingPointAddress" @click="locatePointAddress">
                    <v-icon left size="16">mdi-map-search-outline</v-icon>
                    Localizar endereço
                  </v-btn>
                </div>
                <div ref="pointMap" class="point-map"></div>
                <div class="point-map-footer">
                  <span v-if="form.latitude && form.longitude">{{ addressLocationMessage }}</span>
                  <span v-else>Informe um endereço ou clique no mapa para definir o pin.</span>
                </div>
              </div>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn v-if="isBusinessDialog && businessStep !== 'basic'" text @click="previousBusinessStep">Voltar</v-btn>
          <v-btn v-if="activeKey === 'units' && unitStep !== 'basic'" text @click="previousUnitStep">Voltar</v-btn>
          <v-btn v-if="activeKey === 'points' && pointStep === 'map'" text @click="backToPointForm">Voltar</v-btn>
          <v-btn
            v-if="isBusinessDialog && businessStep !== 'additional'"
            depressed
            color="primary"
            @click="nextBusinessStep"
          >
            Próximo
          </v-btn>
          <v-btn
            v-if="activeKey === 'units' && unitStep !== 'responsible'"
            depressed
            color="primary"
            @click="nextUnitStep"
          >
            Próximo
          </v-btn>
          <v-btn
            v-if="activeKey === 'points' && pointStep === 'form'"
            depressed
            color="primary"
            @click="goToPointMapStep"
          >
            Próximo
          </v-btn>
          <v-btn v-if="showDialogSaveButton" depressed color="primary" :loading="saving" @click="save">{{ isBusinessDialog || activeKey === "units" ? "Finalizar" : "Salvar" }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="unitAnalyticsDialog" max-width="920">
      <v-card class="form-dialog unit-analytics-dialog">
        <v-card-title>
          <div>
            <strong>Analítico da residência</strong>
            <span>{{ selectedUnitAnalyticsLabel || "Histórico financeiro, chamados e alertas relacionados à unidade." }}</span>
          </div>
        </v-card-title>
        <v-card-text class="form-dialog-body">
          <div class="unit-analytics">
            <v-progress-linear v-if="loadingUnitAnalytics" indeterminate color="primary" class="mt-3" />
            <div v-else>
              <div class="unit-analytics-grid">
                <div class="unit-analytics-card">
                  <span>Associado desde</span>
                  <strong>{{ formatDate(unitAnalytics.associated_since) }}</strong>
                </div>
                <div class="unit-analytics-card danger">
                  <span>Inadimplências</span>
                  <strong>{{ money(unitAnalytics.financial.overdue_amount) }}</strong>
                  <small>{{ unitAnalytics.financial.overdue_count }} cobrança(s)</small>
                </div>
                <div class="unit-analytics-card">
                  <span>Pagamentos efetuados</span>
                  <strong>{{ money(unitAnalytics.financial.paid_amount) }}</strong>
                  <small>{{ unitAnalytics.financial.paid_count }} pagamento(s)</small>
                </div>
              </div>

              <div class="unit-analytics-lists">
                <div class="financial-history-panel">
                  <strong>Cobranças recentes</strong>
                  <div v-for="charge in unitAnalytics.financial.latest_charges.slice(0, 4)" :key="`charge-${charge.id}`" class="analytics-row">
                    <span>{{ formatReferenceMonth(charge.reference_month) }} · {{ formatDate(charge.due_date) }}</span>
                    <b>{{ money(charge.amount) }} · {{ optionLabel(charge.status) }}</b>
                  </div>
                  <small v-if="!unitAnalytics.financial.latest_charges.length">Nenhuma cobrança vinculada.</small>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="unitAnalyticsDialog = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="voteDialog" max-width="520">
      <v-card class="form-dialog">
        <v-card-title>
          <div>
            <strong>Registrar voto</strong>
            <span>Informe o voto registrado para a pauta.</span>
          </div>
        </v-card-title>
        <v-card-text>
          <p class="vote-title">{{ selectedPoll ? selectedPoll.title : "" }}</p>
          <v-radio-group v-model="voteOptionId">
            <v-radio
              v-for="option in selectedPollOptions"
              :key="option.id"
              :label="option.label"
              :value="option.id"
            />
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="voteDialog = false">Cancelar</v-btn>
          <v-btn depressed color="primary" :loading="saving" @click="saveVote">Votar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import L from "leaflet";
import BoaruaDataTable from "@/views/boarua/components/BoaruaDataTable.vue";
import ListFilters from "@/views/boarua/components/ListFilters.vue";
import { applyListFilters, initialListFilters, normalizeText, resolveDateRangeFilters } from "@/views/boarua/helpers/listFilters";

export default {
  name: "BoaruaValueModules",
  components: { BoaruaDataTable, ListFilters },
  data() {
    return {
      loading: false,
      saving: false,
      dialog: false,
      editingId: null,
      deletingId: null,
      deleteDialog: false,
      itemToDelete: null,
      unitAnalyticsDialog: false,
      selectedUnitAnalyticsId: null,
      selectedUnitAnalyticsLabel: "",
      voteDialog: false,
      filterDraft: initialListFilters(),
      activeFilters: initialListFilters(),
      items: [],
      form: {},
      validationErrors: {},
      selectedPoll: null,
      voteOptionId: null,
      pointMap: null,
      pointMarker: null,
      pointStep: "form",
      businessStep: "basic",
      unitStep: "basic",
      searchingPointAddress: false,
      members: [],
      groups: [],
      association: null,
      addressOptions: [],
      addressSearch: "",
      selectedAddressSuggestion: null,
      addressLocationPrecision: "",
      addressSearchTimer: null,
      addressSearchRequestId: 0,
      searchingAddress: false,
      draggingFileField: null,
      loadingUnitAnalytics: false,
      unitAnalytics: {
        associated_since: null,
        financial: { paid_count: 0, paid_amount: 0, pending_count: 0, pending_amount: 0, overdue_count: 0, overdue_amount: 0, latest_charges: [] },
        demands: { total: 0, open: 0, latest: [] },
        alerts: { total: 0, open: 0, latest: [] },
      },
      optionLabels: {
        active: "Ativo",
        inactive: "Inativo",
        pending_invite: "Convite pendente",
        invite_rejected: "Convite recusado",
        published: "Publicado",
        draft: "Rascunho",
        planned: "Planejado",
        in_progress: "Em andamento",
        done: "Concluído",
        open: "Aberto",
        closed: "Fechado",
        cancelled: "Cancelado",
        low: "Baixa",
        normal: "Normal",
        high: "Alta",
        urgent: "Urgente",
        casa: "Casa",
        house: "Casa",
        street_house: "Casa",
        apartamento: "Apartamento",
        apartment: "Apartamento",
        condominium: "Condomínio",
        condominio: "Condomínio",
        condo_house: "Casa em condomínio",
        condominium_house: "Casa em condomínio",
        commercial: "Comercial",
        comercial: "Comercial",
        commerce: "Comércio",
        comercio: "Comércio",
        store: "Comércio",
        business: "Comércio",
        mixed: "Misto",
        misto: "Misto",
        announcement: "Comunicado",
        improvement: "Melhoria",
        work: "Obra",
        accountability: "Prestação de contas",
        revenue: "Receita",
        expense: "Despesa",
        camera: "Câmera",
        gatehouse: "Portaria",
        trash_bin: "Lixeira",
        risk_area: "Área de risco",
        square: "Praça",
        patrol: "Ronda",
        reference: "Referência",
        organic: "Orgânico",
        dry: "Seco",
        recyclable: "Reciclável",
        green: "Verde/jardinagem",
        monday: "Segunda-feira",
        tuesday: "Terça-feira",
        wednesday: "Quarta-feira",
        thursday: "Quinta-feira",
        friday: "Sexta-feira",
        saturday: "Sábado",
        sunday: "Domingo",
        security: "Segurança",
        police: "Polícia",
        firefighters: "Bombeiros",
        ambulance: "Ambulância",
        city_hall: "Prefeitura",
        maintenance: "Manutenção",
        general: "Geral",
        market: "Mercado",
        pharmacy: "Farmácia",
        restaurant: "Restaurante",
        petshop: "Pet shop",
        services: "Serviços",
        construction: "Construção",
        health: "Saúde",
        beauty: "Beleza",
        all: "Todos",
        group: "Núcleo",
        representatives: "Representantes",
        none: "Sem recorrência",
        monthly: "Mensal",
        bimonthly: "Bimestral",
        quarterly: "Trimestral",
        semiannual: "Semestral",
        annual: "Anual",
        paid: "Pago",
        pending: "Pendente",
        overdue: "Inadimplente",
        no_charge: "Sem cobrança",
      },
      modules: [
        {
          key: "units",
          routeKey: "residencias",
          label: "Residências",
          title: "Residências e unidades",
          singular: "residência",
          endpoint: "/member-units",
          icon: "mdi-home-city-outline",
          description: "Cadastre as unidades e mantenha os responsáveis vinculados.",
          headers: [
            { text: "Responsável", value: "responsible_display" },
            { text: "Tipo", value: "address_type" },
            { text: "Endereço", value: "address_display" },
            { text: "Pagamento", value: "payment_status" },
            { text: "Ações", value: "actions", align: "end", sortable: false, width: 96 },
          ],
          fields: [
            {
              name: "address_type",
              label: "Tipo",
              type: "select",
              cols: 4,
              items: [
                { text: "Casa", value: "house" },
                { text: "Apartamento", value: "apartment" },
                { text: "Condomínio", value: "condominium" },
                { text: "Comercial", value: "commercial" },
                { text: "Misto", value: "mixed" },
              ],
            },
            { name: "contribution_group_id", label: "Núcleo", type: "select", cols: 4, itemsFrom: "groups" },
            { name: "street", label: "Endereço", type: "address", cols: 12 },
            { name: "number", label: "Número", cols: 4 },
            { name: "complement", label: "Complemento", cols: 8 },
            { name: "zip_code", label: "CEP", cols: 4 },
            { name: "status", label: "Status", type: "select", cols: 8, items: ["active", "inactive"] },
            { name: "responsible_section", type: "section", label: "Responsável da residência", text: "Informe quem receberá o convite para acessar o App e aceitar o vínculo com a associação.", cols: 12 },
            { name: "responsible_name", label: "Nome", cols: 6 },
            { name: "responsible_email", label: "E-mail", type: "email", cols: 6 },
            { name: "responsible_phone", label: "Celular", mask: "(##) #####-####", cols: 4 },
            { name: "responsible_document", label: "CPF", mask: "###.###.###-##", cols: 4 },
            {
              name: "responsible_billing_notice",
              type: "alert",
              cols: 12,
              text: "Ao aceitar o convite, o responsável será vinculado à associação, a residência ficará ativa e poderá receber mensalidades. O aceite ficará registrado.",
            },
          ],
        },
        {
          key: "feed",
          routeKey: "prestacao",
          label: "Prestação",
          title: "Prestação de contas",
          singular: "lançamento",
          endpoint: "/feed-posts",
          icon: "mdi-cash-check",
          description: "Receitas, despesas, fornecedores e observações financeiras para transparência com as residências.",
          financialTypes: ["revenue", "expense"],
          allowDelete: true,
          headers: [
            { text: "Descrição", value: "title" },
            { text: "Categoria", value: "post_type" },
            { text: "Situação", value: "execution_status" },
            { text: "Valor", value: "amount" },
            { text: "Recorrência", value: "recurrence_frequency" },
            { text: "Próximo venc.", value: "next_due_date" },
            { text: "Fornecedor/Origem", value: "vendor_name" },
            { text: "Comprovante", value: "payment_receipt", sortable: false },
            { text: "Status", value: "status" },
            { text: "Ações", value: "actions", align: "end", sortable: false, width: 96 },
          ],
          fields: [
            { name: "title", label: "Descrição do lançamento", cols: 8 },
            { name: "post_type", label: "Categoria", type: "select", cols: 4, items: ["expense", "revenue"], defaultValue: "expense" },
            { name: "execution_status", label: "Situação", type: "select", cols: 4, items: ["planned", "done"], defaultValue: "done" },
            { name: "amount", label: "Valor", type: "currency", cols: 4, allowNegative: false },
            { name: "vendor_name", label: "Fornecedor ou origem", cols: 4 },
            { name: "content", label: "Observações", type: "textarea", cols: 12 },
            {
              name: "payment_receipt_section",
              type: "section",
              label: "Comprovante",
              text: "Anexe o comprovante de pagamento quando o lançamento estiver pago ou concluído.",
              cols: 12,
            },
            { name: "payment_receipt_file", label: "Comprovante de pagamento", type: "file", cols: 12 },
            {
              name: "payment_receipt_notice",
              type: "alert",
              cols: 12,
              text: "O comprovante ficará vinculado ao lançamento para consulta na prestação de contas.",
            },
            {
              name: "recurrence_section",
              type: "section",
              label: "Recorrência",
              text: "Use para receitas ou despesas fixas, como empresa de segurança, limpeza ou contratos recorrentes.",
              cols: 12,
            },
            { name: "recurrence_frequency", label: "Frequência", type: "select", cols: 4, items: ["none", "monthly", "bimonthly", "quarterly", "semiannual", "annual"], defaultValue: "none" },
            { name: "recurrence_start_date", label: "Início da recorrência", type: "date", cols: 4 },
            { name: "recurrence_end_date", label: "Fim da recorrência", type: "date", cols: 4 },
            { name: "next_due_date", label: "Próximo vencimento", type: "date", cols: 4 },
            { name: "status", label: "Status", type: "select", cols: 12, items: ["published", "draft"], defaultValue: "published" },
          ],
        },
        {
          key: "demands",
          routeKey: "prioridades",
          label: "Prioridades",
          title: "Chamados com apoio dos moradores",
          singular: "chamado",
          endpoint: "/demands",
          icon: "mdi-format-list-checks",
          description: "Demandas com contador de apoios para ordenar o que precisa ser resolvido primeiro.",
          headers: [
            { text: "Título", value: "title" },
            { text: "Categoria", value: "category" },
            { text: "Prioridade", value: "priority" },
            { text: "Apoios", value: "support_count" },
            { text: "Status", value: "status" },
            { text: "Ações", value: "actions", align: "end", sortable: false },
          ],
          fields: [
            { name: "title", label: "Título", cols: 8 },
            { name: "category", label: "Categoria", cols: 4 },
            { name: "priority", label: "Prioridade", type: "select", cols: 4, items: ["low", "normal", "high", "urgent"] },
            { name: "street", label: "Rua", cols: 6 },
            { name: "number", label: "Número", cols: 2 },
            { name: "description", label: "Descrição", type: "textarea", cols: 12 },
            { name: "status", label: "Status", type: "select", cols: 12, items: ["open", "in_progress", "closed", "cancelled"] },
          ],
        },
        {
          key: "points",
          routeKey: "mapa",
          label: "Mapa",
          title: "Mapa operacional",
          singular: "ponto",
          endpoint: "/operational-points",
          icon: "mdi-map-marker-radius-outline",
          description: "Câmeras, portarias, lixeiras de rua, áreas de risco, obras, praças e referências do território.",
          headers: [
            { text: "Nome", value: "name" },
            { text: "Tipo", value: "type" },
            { text: "Endereço", value: "address" },
            { text: "Status", value: "status" },
            { text: "Ações", value: "actions", align: "end", sortable: false },
          ],
          fields: [
            { name: "name", label: "Nome", cols: 8 },
            { name: "type", label: "Tipo", type: "select", cols: 4, items: ["camera", "gatehouse", "trash_bin", "risk_area", "work", "square", "patrol", "reference"] },
            { name: "address", label: "Endereço", cols: 12 },
            { name: "description", label: "Descrição", type: "textarea", cols: 12 },
            { name: "status", label: "Status", type: "select", cols: 12, items: ["active", "inactive"] },
          ],
        },
        {
          key: "waste",
          routeKey: "coleta",
          label: "Coleta",
          title: "Horários de coleta",
          singular: "horário de coleta",
          endpoint: "/waste-schedules",
          icon: "mdi-trash-can-outline",
          description: "Agenda de lixo orgânico, seco/reciclável e observações por núcleo ou rota.",
          headers: [
            { text: "Tipo", value: "waste_type" },
            { text: "Horários", value: "schedule_json" },
            { text: "Rota", value: "route_name" },
            { text: "Status", value: "status" },
            { text: "Ações", value: "actions", align: "end", sortable: false },
          ],
          fields: [
            { name: "waste_type", label: "Tipo", type: "select", items: ["organic", "dry", "recyclable", "green"] },
            { name: "schedule_items", label: "Dias e horários", type: "schedule-list", cols: 12 },
            { name: "route_name", label: "Rota/área", cols: 12 },
            { name: "notes", label: "Observações", type: "textarea", cols: 12 },
            { name: "status", label: "Status", type: "select", cols: 12, items: ["active", "inactive"] },
          ],
        },
        {
          key: "contacts",
          routeKey: "emergencia",
          label: "Emergência",
          title: "Telefones de emergência",
          singular: "contato",
          endpoint: "/emergency-contacts",
          icon: "mdi-phone-alert-outline",
          description: "Contatos úteis da segurança, portaria, bombeiros, polícia, prefeitura e prestadores críticos.",
          headers: [
            { text: "Nome", value: "name" },
            { text: "Categoria", value: "category" },
            { text: "Telefone", value: "phone" },
            { text: "Horário", value: "available_hours" },
            { text: "Status", value: "status" },
            { text: "Ações", value: "actions", align: "end", sortable: false },
          ],
          fields: [
            { name: "name", label: "Nome", cols: 8 },
            { name: "category", label: "Categoria", type: "select", cols: 4, items: ["security", "gatehouse", "police", "firefighters", "ambulance", "city_hall", "maintenance", "general"] },
            { name: "phone", label: "Telefone" },
            { name: "secondary_phone", label: "Telefone secundário" },
            { name: "available_hours", label: "Horário de atendimento" },
            { name: "notes", label: "Observações", type: "textarea", cols: 12 },
            { name: "status", label: "Status", type: "select", cols: 12, items: ["active", "inactive"] },
          ],
        },
        {
          key: "businesses",
          routeKey: "comercio",
          label: "Comércio",
          title: "Comércio local e promoções",
          singular: "comércio",
          endpoint: "/local-businesses",
          icon: "mdi-storefront-outline",
          description: "Promoções publicadas pelos comércios no App. Use o backoffice para acompanhar e remover conteúdos inadequados.",
          allowDelete: true,
          headers: [
            { text: "Nome", value: "name" },
            { text: "Categoria", value: "category" },
            { text: "Promoção", value: "promotion_title" },
            { text: "Cupom", value: "coupon_code" },
            { text: "Destaque", value: "is_featured" },
            { text: "Status", value: "status" },
            { text: "Ações", value: "actions", align: "end", sortable: false },
          ],
          fields: [
            { name: "name", label: "Nome do comércio", cols: 8 },
            { name: "category", label: "Categoria", type: "select", cols: 4, items: ["market", "pharmacy", "restaurant", "petshop", "services", "construction", "health", "beauty", "general"], defaultValue: "general" },
            { name: "description", label: "Descrição", type: "textarea", cols: 12 },
            { name: "phone", label: "Telefone", cols: 4 },
            { name: "whatsapp", label: "WhatsApp", cols: 4 },
            { name: "email", label: "E-mail", type: "email", cols: 4 },
            { name: "website_url", label: "Site/link", cols: 6 },
            { name: "instagram_url", label: "Instagram", cols: 6 },
            { name: "address", label: "Endereço", cols: 12 },
            { name: "is_featured", label: "Comércio em destaque", type: "select", cols: 6, items: [{ text: "Sim", value: true }, { text: "Não", value: false }], defaultValue: false },
            { name: "is_premium", label: "Comércio premium", type: "select", cols: 6, items: [{ text: "Sim", value: true }, { text: "Não", value: false }], defaultValue: true },
            { name: "status", label: "Status", type: "select", cols: 12, items: ["active", "inactive"] },
          ],
        },
        {
          key: "patrols",
          routeKey: "rondas",
          label: "Rondas",
          title: "Pontos de ronda",
          singular: "ponto de ronda",
          endpoint: "/patrol-points",
          icon: "mdi-shield-check-outline",
          description: "Pontos que a segurança pode confirmar por QR Code ou localização.",
          headers: [
            { text: "Nome", value: "name" },
            { text: "QR Code", value: "qr_code" },
            { text: "Ordem", value: "sort_order" },
            { text: "Status", value: "status" },
            { text: "Ações", value: "actions", align: "end", sortable: false },
          ],
          fields: [
            { name: "name", label: "Nome", cols: 8 },
            { name: "sort_order", label: "Ordem", type: "number", cols: 4 },
            { name: "latitude", label: "Latitude", type: "number" },
            { name: "longitude", label: "Longitude", type: "number" },
            { name: "description", label: "Descrição", type: "textarea", cols: 12 },
            { name: "status", label: "Status", type: "select", cols: 12, items: ["active", "inactive"] },
          ],
        },
        {
          key: "polls",
          routeKey: "votacoes",
          label: "Votações",
          title: "Votações rápidas",
          singular: "votação",
          endpoint: "/polls",
          icon: "mdi-poll",
          description: "Consultas por local ou núcleo para decisões rápidas.",
          headers: [
            { text: "Título", value: "title" },
            { text: "Público", value: "audience" },
            { text: "Status", value: "status" },
            { text: "Ações", value: "actions", align: "end", sortable: false },
          ],
          fields: [
            { name: "title", label: "Título", cols: 8 },
            { name: "audience", label: "Público", type: "select", cols: 4, items: ["all", "group", "representatives"] },
            { name: "description", label: "Descrição", type: "textarea", cols: 12 },
            { name: "options_text", label: "Opções separadas por vírgula", cols: 12 },
            { name: "status", label: "Status", type: "select", cols: 12, items: ["draft", "open", "closed"] },
          ],
        },
        {
          key: "reports",
          routeKey: "relatorios",
          label: "Relatórios",
          title: "Relatório mensal automático",
          singular: "relatório",
          endpoint: "/monthly-reports",
          icon: "mdi-file-chart-outline",
          description: "Resumo mensal de financeiro, chamados, segurança, rondas, comunicados e assembleias.",
          headers: [
            { text: "Referência", value: "reference_month" },
            { text: "Título", value: "title" },
            { text: "Métricas", value: "metrics_json" },
            { text: "Status", value: "status" },
            { text: "Ações", value: "actions", align: "end", sortable: false },
          ],
          fields: [
            { name: "reference_month", label: "Referência AAAA-MM", cols: 4 },
            { name: "title", label: "Título", cols: 8 },
            { name: "summary", label: "Resumo", type: "textarea", cols: 12 },
            { name: "status", label: "Status", type: "select", cols: 12, items: ["draft", "published"] },
          ],
        },
      ],
    };
  },
  computed: {
    activeModule() {
      const routeModule = this.$route.params.module || this.$route.meta.module;
      return this.modules.find((module) => module.routeKey === routeModule) || this.modules[0];
    },
    activeKey() {
      return this.activeModule.key;
    },
    dialogMaxWidth() {
      if (this.isBusinessDialog || this.activeKey === "units") return 860;
      return 720;
    },
    isBusinessDialog() {
      return this.activeKey === "businesses";
    },
    canCreateActiveModule() {
      return this.activeModule.allowCreate !== false;
    },
    canEditActiveModule() {
      return this.activeModule.allowEdit !== false;
    },
    canDeleteActiveModule() {
      return this.activeModule.allowDelete === true;
    },
    deleteActionTitle() {
      if (this.activeKey === "feed") return "Excluir lançamento";
      return "Excluir promoção inadequada";
    },
    deleteDialogTitle() {
      if (this.activeKey === "feed") return "Excluir prestação";
      return "Excluir registro";
    },
    emptyStateDescription() {
      if (this.activeKey === "businesses") {
        return "As promoções lançadas pelos comércios no App aparecerão aqui para moderação.";
      }
      return "Cadastre dados para demonstrar este módulo.";
    },
    addressLocationMessage() {
      const precision = String(this.addressLocationPrecision || "").toLowerCase();
      if (["street", "approximate", "geometric_center"].includes(precision)) {
        return "Localização aproximada. Ajuste o pin no mapa.";
      }
      return "Pin definido no mapa";
    },
    weekdayOptions() {
      return ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((weekday) => ({
        text: this.optionLabel(weekday),
        value: weekday,
      }));
    },
    wasteScheduleRows() {
      return Array.isArray(this.form.schedule_items) ? this.form.schedule_items : [];
    },
    filterFields() {
      if (["units", "businesses"].includes(this.activeKey)) return [];
      if (this.activeKey === "feed") {
        const fieldByName = (name) => this.activeModule.fields.find((field) => field.name === name);
        return [
          { name: "post_type", label: "Categoria", type: "select", items: this.selectItems(fieldByName("post_type")) },
          { name: "execution_status", label: "Situação", type: "select", items: this.selectItems(fieldByName("execution_status")) },
          { name: "vendor_name", label: "Fornecedor", type: "text" },
          { name: "status", label: "Status", type: "select", items: this.selectItems(fieldByName("status")) },
        ];
      }

      const allowed = ["status", "type", "address_type", "post_type", "execution_status", "priority", "waste_type", "weekday", "category", "audience"];
      return this.activeModule.fields
        .filter((field) => allowed.includes(field.name) && field.type === "select")
        .map((field) => ({
          name: field.name,
          label: field.label,
          type: "select",
          items: this.selectItems(field),
        }));
    },
    filteredItems() {
      const textKeys = this.activeModule.headers.map((header) => header.value).filter((value) => value && value !== "actions");
      if (this.activeKey === "units") textKeys.push("responsible_search");
      return applyListFilters(this.displayItems, this.activeFilters, {
        textKeys,
        dateKeys: ["created_at", "updated_at", "due_date", "next_due_date", "starts_at", "ends_at", "received_at", "reference_month"],
        fieldMatchers: this.activeKey === "feed" ? this.feedFilterMatchers() : {},
      });
    },
    displayItems() {
      if (this.activeKey !== "units") return this.items;
      return (this.items || []).map((item) =>
        Object.assign({}, item, {
          responsible_display: this.unitResponsibleDisplay(item),
          responsible_search: this.unitResponsibleSearch(item),
          address_display: this.unitAddressTableDisplay(item),
          payment_status: item.payment_status || "no_charge",
        })
      );
    },
    visibleFields() {
      if (this.activeKey === "points" && this.pointStep === "form") {
        return this.activeModule.fields.filter((field) => field.name !== "address");
      }

      return this.activeModule.fields;
    },
    businessSteps() {
      return [
        { value: "basic", label: "Informações básicas", icon: "mdi-storefront-outline", description: "Identifique o comércio e descreva o que será exibido no App." },
        { value: "address", label: "Endereço e mapa", icon: "mdi-map-marker-outline", description: "Confirme o endereço e posicione o pin do comércio." },
        { value: "additional", label: "Informações adicionais", icon: "mdi-tune-variant", description: "Complete contatos, links e regras de destaque." },
      ];
    },
    currentBusinessStep() {
      return this.businessSteps.find((step) => step.value === this.businessStep) || this.businessSteps[0];
    },
    unitSteps() {
      return [
        { value: "basic", label: "Dados da residência", icon: "mdi-home-city-outline", description: "Defina tipo, núcleo e status da unidade." },
        { value: "address", label: "Endereço e mapa", icon: "mdi-map-marker-outline", description: "Confirme o endereço e ajuste o pin da residência." },
        { value: "responsible", label: "Responsável", icon: "mdi-account-check-outline", description: "Informe quem receberá o convite e será vinculado à residência." },
      ];
    },
    currentUnitStep() {
      return this.unitSteps.find((step) => step.value === this.unitStep) || this.unitSteps[0];
    },
    businessBasicFields() {
      return this.fieldsByName(["name", "category", "description"]);
    },
    businessAdditionalFields() {
      return this.fieldsByName(["phone", "whatsapp", "email", "website_url", "instagram_url", "is_featured", "is_premium", "status"]);
    },
    showDialogSaveButton() {
      if (this.isBusinessDialog) return this.businessStep === "additional";
      if (this.activeKey === "units") return this.unitStep === "responsible";
      if (this.activeKey === "points") return this.pointStep !== "form";
      return true;
    },
    unitPrimaryFields() {
      return this.unitFields(["address_type", "contribution_group_id", "status"]).map((field) => Object.assign({}, field, { cols: 4 }));
    },
    unitAddressFields() {
      return this.unitFields(["street", "complement"]);
    },
    unitResponsibleFields() {
      return this.unitFields([
        "responsible_section",
        "responsible_name",
        "responsible_email",
        "responsible_phone",
        "responsible_document",
        "responsible_billing_notice",
      ]);
    },
    selectedPollOptions() {
      return this.selectedPoll && this.selectedPoll.options ? this.selectedPoll.options : [];
    },
  },
  watch: {
    "$route.params.module"() {
      this.destroyPointMap();
      this.businessStep = "basic";
      this.unitStep = "basic";
      this.resetFilters();
      this.load();
      if (this.activeKey === "units") this.loadUnitOptions();
    },
    addressSearch(value) {
      if ((this.activeKey === "units" && this.unitStep === "address") || (this.activeKey === "points" && this.pointStep === "map") || (this.isBusinessDialog && this.businessStep === "address")) {
        this.queueAddressSearch(value);
      }
    },
    dialog(value) {
      if (!value) {
        this.destroyPointMap();
        this.pointStep = "form";
        this.businessStep = "basic";
        this.unitStep = "basic";
        this.selectedAddressSuggestion = null;
      }
    },
    businessStep(value) {
      if (!this.isBusinessDialog || !this.dialog) return;
      if (value === "address") {
        this.addressSearch = this.form.address || this.addressSearch || "";
        this.$nextTick(() => {
          this.initPointMap();
          setTimeout(() => this.pointMap && this.pointMap.invalidateSize(), 250);
        });
        return;
      }
      this.destroyPointMap();
    },
    unitStep(value) {
      if (this.activeKey !== "units" || !this.dialog) return;
      if (value === "address") {
        this.addressSearch = this.unitAddressDisplay(this.form) || this.addressSearch || "";
        this.hydrateUnitAddressSelection();
        this.$nextTick(() => {
          this.initPointMap();
          setTimeout(() => this.pointMap && this.pointMap.invalidateSize(), 250);
        });
        return;
      }
      this.destroyPointMap();
    },
  },
  mounted() {
    this.load();
    this.loadUnitOptions();
    this.loadAssociation();
  },
  beforeDestroy() {
    this.destroyPointMap();
    if (this.addressSearchTimer) clearTimeout(this.addressSearchTimer);
  },
  methods: {
    async loadUnitOptions() {
      if (this.activeKey !== "units") return;

      const [membersResponse, groupsResponse] = await Promise.all([
        axios.get("/association-members", { params: { limit: 200 } }).catch(() => null),
        axios.get("/contribution-groups", { params: { limit: 200 } }).catch(() => null),
      ]);

      this.members = membersResponse ? (membersResponse.data.content.data || membersResponse.data.content || []) : [];
      this.groups = groupsResponse ? (groupsResponse.data.content.data || groupsResponse.data.content || []) : [];
    },
    async loadAssociation() {
      try {
        const response = await axios.get("/associations/current/profile");
        this.association = response.data.content || null;
      } catch (error) {
        this.association = null;
      }
    },
    async load() {
      this.loading = true;
      try {
        const params = this.listParams({ limit: 100 });
        if (this.activeKey === "feed") {
          const requests = this.activeModule.financialTypes.map((postType) =>
            axios.get(this.activeModule.endpoint, { params: this.listParams({ limit: 100, post_type: postType }) })
          );
          const responses = await Promise.all(requests);
          this.items = responses
            .flatMap((response) => response.data.content.data || response.data.content || [])
            .filter((item) => this.activeModule.financialTypes.includes(item.post_type));
          return;
        }

        const response = await axios.get(this.activeModule.endpoint, { params });
        this.items = response.data.content.data || response.data.content || [];
      } finally {
        this.loading = false;
      }
    },
    listParams(base = {}) {
      const filters = this.activeFilters || {};
      const search = {};
      const clientOnlyKeys = this.activeKey === "feed" ? ["vendor_name"] : [];

      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (["search", "period", "start_date", "end_date"].includes(key)) return;
        if (clientOnlyKeys.includes(key)) return;
        if (value === "" || value === null || value === undefined) return;
        if (Array.isArray(value) && !value.length) return;
        search[key] = value;
      });

      const params = Object.assign({}, base);
      const range = resolveDateRangeFilters(filters);
      if (filters.search) params.q = filters.search;
      if (range.start) params.start_date = range.start;
      if (range.end) params.end_date = range.end;
      if (Object.keys(search).length) params.search = JSON.stringify(search);

      return params;
    },
    feedFilterMatchers() {
      return {
        vendor_name: (item, value) => {
          const needle = normalizeText(value);
          if (!needle) return true;
          return normalizeText(item && item.vendor_name).includes(needle);
        },
      };
    },
    resetFilters() {
      this.filterDraft = initialListFilters();
      this.activeFilters = initialListFilters();
    },
    unitFields(names) {
      return names
        .map((name) => this.activeModule.fields.find((field) => field.name === name))
        .filter(Boolean);
    },
    fieldsByName(names) {
      return names
        .map((name) => this.activeModule.fields.find((field) => field.name === name))
        .filter(Boolean);
    },
    async applyFilters(filters) {
      this.activeFilters = Object.assign(initialListFilters(), filters || {});
      await this.load();
    },
    mobileCardTitle(item) {
      const preferredKeys = this.activeKey === "units" ? ["responsible_display", "address_display"] : ["name", "title", "street", "description"];
      const key = preferredKeys.find((field) => this.hasMobileCardValue(item && item[field]));
      if (key) return this.mobileDisplayValue(item, key);

      const header = this.activeModule.headers.find((entry) => entry.value && entry.value !== "actions");
      return header ? this.mobileDisplayValue(item, header.value) : this.activeModule.singular;
    },
    mobileCardSubtitle(item) {
      if (this.activeKey === "units") return this.unitAddressTableDisplay(item);
      const preferredKeys = ["category", "type", "address_type", "post_type"];
      const key = preferredKeys.find((field) => this.hasMobileCardValue(item && item[field]));
      return key ? this.mobileDisplayValue(item, key) : "";
    },
    mobileCardStatus(item) {
      if (this.activeKey === "units") return this.paymentStatusLabel(item);
      if (!this.hasMobileCardValue(item && item.status)) return "";
      return this.optionLabel(item.status);
    },
    mobileStatusColor(item) {
      if (this.activeKey === "units") return this.paymentStatusColor(item);
      return "secondary";
    },
    mobileStatusTextColor(item) {
      if (this.activeKey === "units") return this.paymentStatusTextColor(item);
      return "primary";
    },
    mobileCardFields(item) {
      const hiddenKeys = ["name", "title", "category", "type", "address_type", "post_type", "responsible_display", "address_display", "payment_status", "status", "actions"];
      return this.activeModule.headers
        .filter((header) => header.value && !hiddenKeys.includes(header.value))
        .map((header) => ({
          text: header.text,
          value: header.value,
          display: this.mobileDisplayValue(item, header.value),
        }))
        .filter((field) => this.hasMobileCardValue(field.display));
    },
    mobileDisplayValue(item, key) {
      if (!item || !key) return "";
      const value = item[key];
      if (!this.hasMobileCardValue(value)) return "";

      if (["execution_status", "priority", "waste_type", "weekday", "audience", "category", "type", "address_type", "post_type", "payment_status", "status"].includes(key)) {
        return this.optionLabel(value);
      }
      if (key === "is_featured" || key === "is_premium") return value ? "Sim" : "Não";
      if (key === "amount") return this.money(value);
      if (["next_due_date", "due_date", "starts_at", "ends_at", "received_at"].includes(key)) return this.formatDate(value);
      if (key === "reference_month") return this.formatReferenceMonth(value);
      if (key === "recurrence_frequency") return this.optionLabel(value || "none");
      if (key === "title") return this.displayTitle(item);
      if (key === "schedule_json") return this.scheduleSummary(item);
      if (key === "metrics_json") return this.reportSummary(item);
      if (key === "support_count") return String(value || 0);
      if (key === "responsible_display") return this.unitResponsibleDisplay(item);
      if (key === "address_display") return this.unitAddressTableDisplay(item);
      return String(value);
    },
    hasMobileCardValue(value) {
      if (value === null || value === undefined) return false;
      if (typeof value === "boolean") return true;
      const text = String(value).trim();
      return text !== "" && text !== "-";
    },
    unitResponsibleDisplay(item = {}) {
      const name = this.unitResponsibleName(item);
      const document = this.unitResponsibleDocumentDisplay(item);
      if (name && document) return `${name} · ${document}`;
      return name || document || "-";
    },
    unitResponsibleSearch(item = {}) {
      const document = this.unitResponsibleDocument(item);
      return [this.unitResponsibleName(item), this.unitResponsibleEmail(item), this.unitResponsiblePhone(item), document, this.formatCpf(document)]
        .filter(Boolean)
        .join(" ");
    },
    unitAddressTableDisplay(item = {}) {
      const street = item.street || item.address || item.address_normalized || "";
      const number = item.number || item.house_number || "";
      if (street && number && !String(street).includes(String(number))) return `${street}, ${number}`;
      return street || number || "-";
    },
    unitResponsibleName(item = {}) {
      const member = item.member || {};
      const user = item.user || member.user || {};
      return this.capitalizePersonName(item.responsible_name || item.owner_name || member.name || user.name || "");
    },
    unitResponsibleEmail(item = {}) {
      const member = item.member || {};
      const user = item.user || member.user || {};
      return item.responsible_email || item.owner_email || member.email || user.email || "";
    },
    unitResponsiblePhone(item = {}) {
      const member = item.member || {};
      const user = item.user || member.user || {};
      return item.responsible_phone || item.owner_phone || member.phone || user.phone || user.phone_number || "";
    },
    unitResponsibleDocument(item = {}) {
      const member = item.member || {};
      const user = item.user || member.user || {};
      return item.responsible_document || item.owner_document || member.document || user.document || user.cpf || "";
    },
    unitResponsibleDocumentDisplay(item = {}) {
      return this.formatCpf(this.unitResponsibleDocument(item));
    },
    capitalizePersonName(value) {
      const particles = ["da", "das", "de", "di", "do", "dos", "du", "e"];
      return String(value || "")
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .map((part, index) => {
          if (index > 0 && particles.includes(part)) return part;
          return part.charAt(0).toUpperCase() + part.slice(1);
        })
        .join(" ");
    },
    paymentStatusLabel(item = {}) {
      return item.payment_status_label || this.optionLabel(item.payment_status || "no_charge");
    },
    paymentStatusColor(item = {}) {
      const status = String(item.payment_status || "no_charge").toLowerCase();
      if (status === "paid") return "success";
      if (status === "overdue") return "error";
      if (status === "pending") return "warning";
      return "secondary";
    },
    paymentStatusTextColor(item = {}) {
      const status = String(item.payment_status || "no_charge").toLowerCase();
      return status === "pending" ? "primary" : "white";
    },
    formatCpf(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length !== 11) return value || "";
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
    },
    async openDialog(item = null) {
      if ((item && !this.canEditActiveModule) || (!item && !this.canCreateActiveModule)) return;
      this.destroyPointMap();
      this.pointStep = "form";
      this.businessStep = "basic";
      this.unitStep = "basic";
      this.selectedAddressSuggestion = null;
      this.addressLocationPrecision = "";
      this.validationErrors = {};
      this.unitAnalytics = this.emptyUnitAnalytics();
      const editItem = item ? await this.loadEditItem(item) : null;
      this.form = editItem ? this.hydrateFormForEdit(editItem) : this.defaultForm();
      if (this.form.latitude && this.form.longitude) {
        this.addressLocationPrecision = "manual";
      }
      if (this.activeKey === "units") {
        this.addressSearch = this.unitAddressDisplay(this.form);
        const addressOption = this.addressOptionFromForm();
        this.selectedAddressSuggestion = addressOption;
        this.addressOptions = addressOption ? [addressOption] : [];
      }
      if (this.activeKey === "points") {
        this.addressSearch = this.form.address || "";
      }
      if (this.isBusinessDialog) {
        this.addressSearch = this.form.address || "";
      }
      if (this.activeKey === "polls" && this.form.options) {
        this.form.options_text = this.form.options.map((option) => option.label).join(", ");
      }
      if (this.activeKey === "waste") {
        this.$set(this.form, "schedule_items", this.parseScheduleItems(this.form));
      }
      if (this.activeKey === "feed") {
        this.$set(this.form, "payment_receipt_file", null);
      }
      this.dialog = true;
      this.$nextTick(() => {
        this.resetDialogScroll();
      });
    },
    async loadEditItem(item) {
      if (!item || !item.id || !this.activeModule.endpoint) return item;

      this.editingId = item.id;
      try {
        const response = await axios.get(`${this.activeModule.endpoint}/${item.id}`);
        return response.data.content || item;
      } catch (error) {
        return item;
      } finally {
        this.editingId = null;
      }
    },
    emptyUnitAnalytics() {
      return {
        associated_since: null,
        financial: { paid_count: 0, paid_amount: 0, pending_count: 0, pending_amount: 0, overdue_count: 0, overdue_amount: 0, latest_charges: [] },
        demands: { total: 0, open: 0, latest: [] },
        alerts: { total: 0, open: 0, latest: [] },
      };
    },
    async loadUnitAnalytics(unitId) {
      this.loadingUnitAnalytics = true;
      try {
        const response = await axios.get(`/member-units/${unitId}/analytics`);
        const base = this.emptyUnitAnalytics();
        const content = response.data.content || {};
        this.unitAnalytics = Object.assign(base, content, {
          financial: Object.assign(base.financial, content.financial || {}),
          demands: Object.assign(base.demands, content.demands || {}),
          alerts: Object.assign(base.alerts, content.alerts || {}),
        });
      } catch (error) {
        this.unitAnalytics = this.emptyUnitAnalytics();
      } finally {
        this.loadingUnitAnalytics = false;
      }
    },
    async openUnitAnalytics(item = {}) {
      if (!item || !item.id) return;
      this.selectedUnitAnalyticsId = item.id;
      this.selectedUnitAnalyticsLabel = [this.unitResponsibleName(item), this.unitAddressTableDisplay(item)]
        .filter((part) => part && part !== "-")
        .join(" · ");
      this.unitAnalytics = this.emptyUnitAnalytics();
      this.unitAnalyticsDialog = true;
      await this.loadUnitAnalytics(item.id);
    },
    hydrateFormForEdit(item = {}) {
      const form = Object.assign(this.defaultForm(), item);

      if (this.activeKey === "units") {
        const member = item.member || {};
        const user = member.user || {};
        if (!form.street) form.street = member.street || "";
        if (!form.number) form.number = member.number || "";
        if (!form.complement) form.complement = member.complement || "";
        if (!form.zip_code) form.zip_code = member.zip_code || "";
        if (!form.address_normalized) form.address_normalized = this.normalizedAddressLabel(form.street, form.number);
        if (!form.responsible_name) form.responsible_name = user.name || member.name || "";
        if (!form.responsible_email) form.responsible_email = user.email || member.email || "";
        if (!form.responsible_phone) form.responsible_phone = user.phone || user.phone_number || member.phone || "";
        if (!form.responsible_document) form.responsible_document = user.document || user.cpf || member.document || "";
        form.responsible_phone = this.formatPhone(form.responsible_phone);
        form.responsible_document = this.formatCpf(form.responsible_document);
      }

      return form;
    },
    hydrateUnitAddressSelection() {
      const addressOption = this.addressOptionFromForm();
      this.addressSearch = this.unitAddressDisplay(this.form);
      this.selectedAddressSuggestion = addressOption;
      this.addressOptions = addressOption ? [addressOption].concat(this.addressOptions.filter((item) => item.place_id !== addressOption.place_id)) : this.addressOptions;
    },
    resetDialogScroll() {
      const body = document.querySelector(".v-dialog__content--active .form-dialog-body");
      if (body) body.scrollTop = 0;
    },
    unitAddressDisplay(form = {}) {
      return form.address_normalized || this.normalizedAddressLabel(form.street, form.number) || form.street || "";
    },
    addressOptionFromForm() {
      if (this.activeKey !== "units") return null;

      const displayName = this.unitAddressDisplay(this.form);
      if (!displayName && !this.form.street) return null;

      return {
        place_id: this.form.address_integration_id || `member-unit:${this.form.id || "new"}`,
        display_name: displayName || this.form.street,
        lat: this.form.latitude || null,
        lon: this.form.longitude || null,
        source: this.form.address_integration_source || "saved",
        address: {
          road: this.form.street || "",
          house_number: this.form.number || "",
          suburb: this.form.neighborhood || "",
          neighbourhood: this.form.neighborhood || "",
          city: this.form.city || "",
          town: this.form.city || "",
          state: this.form.state || "",
          postcode: this.form.zip_code || "",
          country: "Brasil",
          country_code: "br",
        },
      };
    },
    defaultForm() {
      const form = {};
      this.activeModule.fields.forEach((field) => {
        if (["section", "alert"].includes(field.type)) return;
        if (Object.prototype.hasOwnProperty.call(field, "defaultValue")) form[field.name] = field.defaultValue;
        else if (field.name === "status") form[field.name] = field.items ? this.optionValue(field.items[0]) : "active";
        else if (field.type === "file") form[field.name] = null;
        else form[field.name] = "";
      });
      if (this.activeKey === "reports") form.reference_month = this.currentReferenceMonth();
      if (this.activeKey === "units") form.address_type = "house";
      if (this.activeKey === "businesses") {
        form.latitude = "";
        form.longitude = "";
      }
      if (this.activeKey === "waste") form.schedule_items = [this.emptyScheduleItem()];
      return form;
    },
    validateUnitPayload(payload = {}, parsedAddress = {}) {
      const errors = {};
      const required = [
        { name: "address_type", label: "Tipo" },
        { name: "contribution_group_id", label: "Núcleo" },
        { name: "status", label: "Status" },
        { name: "responsible_name", label: "Nome do responsável" },
        { name: "responsible_email", label: "E-mail do responsável" },
        { name: "responsible_phone", label: "Celular do responsável" },
        { name: "responsible_document", label: "CPF do responsável" },
      ];

      required.forEach((field) => {
        if (!this.hasValue(payload[field.name])) {
          errors[field.name] = "Campo obrigatório";
        }
      });

      if (!this.hasValue(payload.street)) {
        errors.street = "Informe o endereço";
      } else if (!this.hasValue(payload.number || parsedAddress.number)) {
        errors.street = "Informe o endereço com número";
      }

      if (this.hasValue(payload.responsible_email) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(payload.responsible_email).trim())) {
        errors.responsible_email = "Informe um e-mail válido";
      }
      if (this.hasValue(payload.responsible_phone) && this.onlyDigits(payload.responsible_phone).length !== 11) {
        errors.responsible_phone = "Informe um celular válido";
      }
      if (this.hasValue(payload.responsible_document) && this.onlyDigits(payload.responsible_document).length !== 11) {
        errors.responsible_document = "Informe um CPF válido";
      }

      this.validationErrors = errors;
      const labels = this.validationErrorLabels(errors);
      if (!labels.length) return true;

      this.notifyError(`Preencha os campos obrigatórios: ${labels.join(", ")}`);
      this.$nextTick(() => this.scrollToFirstValidationError());
      return false;
    },
    validateBusinessPayload(payload = {}) {
      const errors = {};

      if (!this.hasValue(payload.name)) errors.name = "Campo obrigatório";
      if (!this.hasValue(payload.category)) errors.category = "Campo obrigatório";

      this.validationErrors = errors;
      const labels = this.validationErrorLabels(errors);
      if (!labels.length) return true;

      this.notifyError(`Preencha os campos obrigatórios: ${labels.join(", ")}`);
      this.$nextTick(() => this.scrollToFirstValidationError());
      return false;
    },
    validationErrorLabels(errors = {}) {
      const labels = {
        address_type: "Tipo",
        contribution_group_id: "Núcleo",
        status: "Status",
        street: "Endereço com número",
        name: "Nome",
        category: "Categoria",
        responsible_name: "Nome",
        responsible_email: "E-mail",
        responsible_phone: "Celular",
        responsible_document: "CPF",
      };

      return Object.keys(errors).map((field) => labels[field] || field);
    },
    hasValue(value) {
      return value !== null && value !== undefined && String(value).trim() !== "";
    },
    fieldErrors(name) {
      const error = this.validationErrors && this.validationErrors[name];
      return error ? [error] : [];
    },
    clearValidationError(name) {
      if (this.validationErrors && this.validationErrors[name]) {
        this.$delete(this.validationErrors, name);
      }
    },
    handleMaskedFieldInput(field = {}) {
      this.clearValidationError(field.name);
      if (!field.mask || !field.name) return;
      const value = this.form[field.name];
      const masked = this.applyKnownMask(value, field.mask);
      if (masked !== value) this.$set(this.form, field.name, masked);
    },
    applyKnownMask(value, mask) {
      if (mask === "(##) #####-####") return this.formatPhone(value);
      if (mask === "###.###.###-##") return this.formatCpf(value);
      return value;
    },
    formatPhone(value) {
      const digits = this.onlyDigits(value).slice(0, 11);
      if (digits.length <= 2) return digits;
      if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    },
    onlyDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },
    onUnitAddressChange(fieldName, item) {
      this.clearValidationError(fieldName);
      this.applyAddressSuggestion(item);
    },
    scrollToFirstValidationError() {
      const body = this.$el.querySelector(".form-dialog-body");
      const firstError = this.$el.querySelector(".v-input.error--text");
      if (!body || !firstError) return;

      body.scrollTo({
        top: Math.max(0, firstError.offsetTop - body.offsetTop - 16),
        behavior: "smooth",
      });
    },
    initPointMap() {
      if (!this.$refs.pointMap || this.pointMap) return;

      const center = this.form.latitude && this.form.longitude
        ? [Number(this.form.latitude), Number(this.form.longitude)]
        : [-30.0346, -51.2177];

      this.pointMap = L.map(this.$refs.pointMap, { zoomControl: true }).setView(center, this.form.latitude ? 16 : 13);
      this.pointMap.attributionControl.setPrefix(false);
      L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
        attribution: "&copy; OpenStreetMap &copy; CARTO",
        subdomains: "abcd",
        maxZoom: 20,
      }).addTo(this.pointMap);

      this.pointMap.on("click", (event) => {
        this.setPointLocation(event.latlng.lat, event.latlng.lng, true);
      });

      if (this.form.latitude && this.form.longitude) {
        this.setPointLocation(this.form.latitude, this.form.longitude, false);
      }

      setTimeout(() => this.pointMap && this.pointMap.invalidateSize(), 250);
    },
    destroyPointMap() {
      if (this.pointMap) {
        this.pointMap.remove();
      }
      this.pointMap = null;
      this.pointMarker = null;
    },
    goToPointMapStep() {
      this.pointStep = "map";
      this.addressSearch = this.form.address || this.addressSearch || "";
      this.$nextTick(() => {
        this.initPointMap();
        setTimeout(() => this.pointMap && this.pointMap.invalidateSize(), 250);
      });
    },
    backToPointForm() {
      this.pointStep = "form";
      this.destroyPointMap();
    },
    nextBusinessStep() {
      if (this.businessStep === "basic") {
        const errors = {};
        if (!this.hasValue(this.form.name)) errors.name = "Campo obrigatório";
        if (!this.hasValue(this.form.category)) errors.category = "Campo obrigatório";
        this.validationErrors = Object.assign({}, this.validationErrors, errors);
        if (Object.keys(errors).length) {
          this.notifyError("Preencha os dados básicos antes de continuar");
          this.$nextTick(() => this.scrollToFirstValidationError());
          return;
        }
        this.businessStep = "address";
        return;
      }
      if (this.businessStep === "address") {
        this.businessStep = "additional";
      }
    },
    previousBusinessStep() {
      if (this.businessStep === "additional") {
        this.businessStep = "address";
        return;
      }
      if (this.businessStep === "address") {
        this.businessStep = "basic";
      }
    },
    nextUnitStep() {
      if (this.unitStep === "basic") {
        const errors = {};
        if (!this.hasValue(this.form.address_type)) errors.address_type = "Campo obrigatório";
        if (!this.hasValue(this.form.contribution_group_id)) errors.contribution_group_id = "Campo obrigatório";
        if (!this.hasValue(this.form.status)) errors.status = "Campo obrigatório";
        this.validationErrors = Object.assign({}, this.validationErrors, errors);
        if (Object.keys(errors).length) {
          this.notifyError("Preencha os dados da residência antes de continuar");
          this.$nextTick(() => this.scrollToFirstValidationError());
          return;
        }
        this.unitStep = "address";
        return;
      }

      if (this.unitStep === "address") {
        const parsedAddress = this.parseAddressParts(this.form.street || "");
        const errors = {};
        if (!this.hasValue(this.form.street)) {
          errors.street = "Informe o endereço";
        } else if (!this.hasValue(this.form.number || parsedAddress.number)) {
          errors.street = "Informe o endereço com número";
        }
        this.validationErrors = Object.assign({}, this.validationErrors, errors);
        if (Object.keys(errors).length) {
          this.notifyError("Confirme o endereço antes de continuar");
          this.$nextTick(() => this.scrollToFirstValidationError());
          return;
        }
        this.unitStep = "responsible";
      }
    },
    previousUnitStep() {
      if (this.unitStep === "responsible") {
        this.unitStep = "address";
        return;
      }
      if (this.unitStep === "address") {
        this.unitStep = "basic";
      }
    },
    setPointLocation(latitude, longitude, centerMap = true) {
      const lat = Number(latitude);
      const lng = Number(longitude);
      if (!this.isValidMapCoordinate(lat, lng)) return;

      this.$set(this.form, "latitude", lat);
      this.$set(this.form, "longitude", lng);

      if (!this.pointMap) return;

      if (!this.pointMarker) {
        this.pointMarker = L.marker([lat, lng], {
          draggable: true,
          icon: L.divIcon({
            className: "point-location-marker",
            html: '<span><i class="mdi mdi-map-marker"></i></span>',
            iconSize: [34, 34],
            iconAnchor: [17, 31],
          }),
        }).addTo(this.pointMap);
        this.pointMarker.on("dragend", () => {
          const position = this.pointMarker.getLatLng();
          this.addressLocationPrecision = "manual";
          this.setPointLocation(position.lat, position.lng, false);
        });
      } else {
        this.pointMarker.setLatLng([lat, lng]);
      }

      if (centerMap) {
        this.pointMap.setView([lat, lng], Math.max(this.pointMap.getZoom(), 16));
      }
    },
    async locatePointAddress() {
      if (this.activeKey === "units" && this.addressSearch) {
        const parsed = this.syncUnitAddressFromSearch(this.addressSearch);
        if (parsed.street && (!this.form.street || this.form.street === this.addressSearch)) {
          this.$set(this.form, "street", parsed.street);
        }
      }
      if (this.isBusinessDialog && this.addressSearch) {
        this.$set(this.form, "address", this.addressSearch);
      } else if (this.activeKey === "points" && this.addressSearch && !this.form.address) {
        this.$set(this.form, "address", this.addressSearch);
      }
      const address = this.activeKey === "units"
        ? [this.form.street, this.form.number].filter(Boolean).join(", ")
        : String(this.form.address || this.addressSearch || "").trim();
      if (!address) {
        this.notifyError("Informe o endereço");
        return;
      }
      if (this.activeKey === "points" || this.isBusinessDialog) {
        this.addressSearch = address;
      }

      this.searchingPointAddress = true;
      try {
        const results = await this.fetchAddressCandidates(address, 6, { snapToRoad: true });
        const ranked = this.rankAddressResults(results, address);
        const item = await this.resolveAddressPinCandidate(ranked, address);
        if (!this.hasAddressCoordinates(item)) {
          this.notifyError("Endereço não encontrado");
          return;
        }
        if (this.activeKey === "units" || this.activeKey === "points" || this.isBusinessDialog) this.applyAddressSuggestion(item);
        this.addressLocationPrecision = item.geocode_precision || item.location_type || "";
        this.setPointLocation(item.lat, item.lon, true);
      } catch (error) {
        this.notifyError("Não foi possível localizar o endereço");
      } finally {
        this.searchingPointAddress = false;
      }
    },
    async resolveAddressPinCandidate(rankedResults = [], address = "") {
      const direct = rankedResults.find((item) => this.hasAddressCoordinates(item));
      if (direct) return direct;

      const fallback = rankedResults[0];
      const fallbackQueries = [
        fallback && fallback.display_name,
        fallback ? this.pointAddressLabel(fallback, this.addressRoad(fallback), this.addressNumber(fallback, this.splitAddressNumber(address).number)) : "",
        address,
      ].filter(Boolean);

      for (const query of [...new Set(fallbackQueries)]) {
        const results = await this.fetchAddressCandidates(query, 6, { geocoderOnly: true, snapToRoad: true });
        const geocoded = this.rankAddressResults(results, query).find((item) => this.hasAddressCoordinates(item));
        if (geocoded) {
          return Object.assign({}, fallback || {}, geocoded, {
            display_name: (fallback && fallback.display_name) || geocoded.display_name,
            address: Object.assign({}, geocoded.address || {}, (fallback && fallback.address) || {}),
          });
        }
      }

      return fallback || null;
    },
    hasAddressCoordinates(item) {
      const lat = Number(item && item.lat);
      const lon = Number(item && item.lon);
      return this.isValidMapCoordinate(lat, lon);
    },
    addressPrecisionScore(result = {}) {
      const precision = String(result.geocode_precision || "").toLowerCase();
      const source = String(result.source || result.geocoded_source || "").toLowerCase();
      if (source === "google" && precision === "exact") return 80;
      if (precision === "exact") return 60;
      if (source === "google") return 35;
      if (precision === "street") return 20;
      if (precision === "approximate") return 10;
      return 0;
    },
    async save() {
      this.saving = true;
      try {
        const payload = Object.assign({}, this.form);
        if (this.activeKey === "units") {
          payload.apartment = null;
          payload.house_number = null;
          const parsedAddress = this.splitAddressNumber(this.addressSearch);
          if (!payload.street && parsedAddress.street) payload.street = parsedAddress.street;
          if (!payload.number && parsedAddress.number) payload.number = parsedAddress.number;
          payload.address_normalized = this.normalizedAddressLabel(payload.street, payload.number);
          if (!this.validateUnitPayload(payload, parsedAddress)) {
            this.saving = false;
            return;
          }
          if (!payload.address_integration_source && payload.address_integration_id) payload.address_integration_source = "nominatim";
          payload.responsible_phone = this.onlyDigits(payload.responsible_phone);
          payload.responsible_document = this.onlyDigits(payload.responsible_document);
        }
        if (this.activeKey === "polls") {
          payload.options = String(payload.options_text || "")
            .split(",")
            .map((label) => label.trim())
            .filter(Boolean);
          delete payload.options_text;
        }
        if (this.activeKey === "units" && !payload.street && this.addressSearch) {
          const parsed = this.splitAddressNumber(this.addressSearch);
          payload.street = parsed.street || this.addressSearch;
          if (!payload.number && parsed.number) payload.number = parsed.number;
        }
        if (this.activeKey === "units" && payload.street) {
          const parsed = this.splitAddressNumber(payload.street);
          if (parsed.number) {
            payload.street = parsed.street;
            if (!payload.number) payload.number = parsed.number;
          }
        }
        if (this.activeKey === "businesses" && !this.validateBusinessPayload(payload)) {
          this.saving = false;
          return;
        }
        if (this.activeKey === "feed") {
          await this.attachPaymentReceipt(payload);
          const amount = this.parseMoneyFilter(payload.amount);
          payload.amount = amount === null ? null : amount;
          payload.post_type = this.activeModule.financialTypes.includes(payload.post_type) ? payload.post_type : "expense";
          payload.recurrence_frequency = payload.recurrence_frequency || "none";
          if (payload.recurrence_frequency === "none") {
            payload.recurrence_start_date = null;
            payload.recurrence_end_date = null;
          }
        }
        if (this.activeKey === "waste") {
          payload.schedule_items = this.normalizeScheduleItems(payload.schedule_items);
          const firstSchedule = payload.schedule_items[0] || this.emptyScheduleItem();
          payload.weekday = firstSchedule.weekday;
          payload.starts_at = firstSchedule.starts_at || null;
          payload.ends_at = firstSchedule.ends_at || null;
          payload.schedule_json = JSON.stringify(payload.schedule_items);
        }

        let response = null;
        if (payload.id) {
          response = await axios.patch(`${this.activeModule.endpoint}/${payload.id}`, payload);
        } else {
          response = await axios.post(this.activeModule.endpoint, payload);
        }

        this.dialog = false;
        await this.load();
        const content = response && response.data ? response.data.content : null;
        this.notifySuccess(content && content.status === "pending_invite" ? "Residência salva como pendente e convite enviado" : "Registro salvo");
      } catch (error) {
        this.notifyError("Não foi possível salvar o registro");
      } finally {
        this.saving = false;
      }
    },
    async supportDemand(item) {
      try {
        await axios.post(`/demands/${item.id}/support`, {});
        await this.load();
        this.notifySuccess("Apoio registrado");
      } catch (error) {
        this.notifyError("Não foi possível registrar apoio");
      }
    },
    async deleteItem(item) {
      if (!item || !item.id || !this.canDeleteActiveModule) return;

      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      if (this.deletingId) return;
      this.deleteDialog = false;
      this.itemToDelete = null;
    },
    async confirmDeleteItem() {
      const item = this.itemToDelete;
      if (!item || !item.id || !this.canDeleteActiveModule) return;
      this.deletingId = item.id;
      try {
        await axios.delete(`${this.activeModule.endpoint}/${item.id}`);
        await this.load();
        this.deleteDialog = false;
        this.itemToDelete = null;
        this.notifySuccess(this.deleteSuccessMessage());
      } catch (error) {
        this.notifyError(this.deleteErrorMessage());
      } finally {
        this.deletingId = null;
      }
    },
    deleteItemLabel(item = {}) {
      return item.title || item.promotion_title || item.name || `${this.activeModule.singular} ${item.id}`;
    },
    deleteConfirmMessage(label) {
      if (this.activeKey === "feed") {
        return `Excluir "${label}"? Esta ação remove a prestação de contas e seu reflexo no fluxo de caixa.`;
      }
      return `Excluir "${label}"? Esta ação remove a promoção da vitrine dos moradores.`;
    },
    deleteSuccessMessage() {
      if (this.activeKey === "feed") return "Prestação removida";
      return "Promoção removida da vitrine";
    },
    deleteErrorMessage() {
      if (this.activeKey === "feed") return "Não foi possível remover a prestação";
      return "Não foi possível remover a promoção";
    },
    openVoteDialog(item) {
      this.selectedPoll = item;
      this.voteOptionId = item.options && item.options[0] ? item.options[0].id : null;
      this.voteDialog = true;
    },
    async saveVote() {
      if (!this.selectedPoll || !this.voteOptionId) return;
      this.saving = true;
      try {
        await axios.post(`/polls/${this.selectedPoll.id}/vote`, { poll_option_id: this.voteOptionId });
        this.voteDialog = false;
        await this.load();
        this.notifySuccess("Voto registrado");
      } catch (error) {
        this.notifyError("Não foi possível registrar voto");
      } finally {
        this.saving = false;
      }
    },
    async generateReport() {
      this.saving = true;
      try {
        await axios.post("/monthly-reports/generate", { reference_month: this.currentReferenceMonth() });
        await this.load();
        this.notifySuccess("Relatório gerado");
      } catch (error) {
        this.notifyError("Não foi possível gerar relatório");
      } finally {
        this.saving = false;
      }
    },
    async downloadReportPdf(item) {
      if (!item || !item.id) return;
      try {
        const response = await axios.get(`/monthly-reports/${item.id}/pdf`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `relatorio-mensal-${item.reference_month || item.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.notifyError("Não foi possível baixar o PDF");
      }
    },
    currentReferenceMonth() {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    },
    emptyScheduleItem() {
      return { weekday: "monday", starts_at: "", ends_at: "" };
    },
    addScheduleItem() {
      const rows = Array.isArray(this.form.schedule_items) ? this.form.schedule_items : [];
      rows.push(this.emptyScheduleItem());
      this.$set(this.form, "schedule_items", rows);
    },
    removeScheduleItem(index) {
      const rows = Array.isArray(this.form.schedule_items) ? this.form.schedule_items.slice() : [];
      if (rows.length <= 1) return;
      rows.splice(index, 1);
      this.$set(this.form, "schedule_items", rows);
    },
    parseScheduleItems(item) {
      const source = item.schedule_items || item.schedule || item.schedule_json;
      if (Array.isArray(source)) return this.normalizeScheduleItems(source);

      if (typeof source === "string" && source.trim()) {
        try {
          const parsed = JSON.parse(source);
          if (Array.isArray(parsed)) return this.normalizeScheduleItems(parsed);
        } catch (error) {
          // Mantém compatibilidade com registros antigos abaixo.
        }
      }

      if (item.weekday) {
        return this.normalizeScheduleItems([{
          weekday: item.weekday,
          starts_at: item.starts_at || "",
          ends_at: item.ends_at || "",
        }]);
      }

      return [this.emptyScheduleItem()];
    },
    normalizeScheduleItems(items) {
      const schedules = (Array.isArray(items) ? items : [])
        .map((item) => ({
          weekday: item.weekday || "monday",
          starts_at: item.starts_at || "",
          ends_at: item.ends_at || "",
        }))
        .filter((item) => item.weekday);

      return schedules.length ? schedules : [this.emptyScheduleItem()];
    },
    scheduleSummary(item) {
      const schedules = this.parseScheduleItems(item);
      return schedules
        .map((schedule) => {
          const range = schedule.starts_at && schedule.ends_at
            ? ` das ${this.shortTime(schedule.starts_at)} às ${this.shortTime(schedule.ends_at)}`
            : "";
          return `${this.optionLabel(schedule.weekday)}${range}`;
        })
        .join("; ");
    },
    shortTime(value) {
      const text = String(value || "");
      return text ? text.slice(0, 5).replace(":00", "h").replace(":", "h") : "";
    },
    money(value) {
      return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    parseMoneyFilter(value) {
      if (value === "" || value === null || value === undefined) return null;
      if (typeof value === "number") return Number.isFinite(value) ? value : null;
      const normalized = String(value)
        .replace(/[^\d,.-]/g, "")
        .replace(/\./g, "")
        .replace(",", ".");
      const amount = Number(normalized);
      return Number.isFinite(amount) ? amount : null;
    },
    formatDate(value) {
      if (!value) return "-";
      if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
        const [year, month, day] = String(value).split("-");
        return `${day}/${month}/${year}`;
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleDateString("pt-BR");
    },
    formatReferenceMonth(value) {
      const match = String(value || "").match(/^(\d{4})-(\d{2})/);
      if (!match) return value || "-";
      return `${match[2]}/${match[1]}`;
    },
    displayTitle(item = {}) {
      const title = item.title || "";
      if (this.activeKey !== "feed") return title || "-";
      return this.normalizeFinancialDescription(title) || "-";
    },
    normalizeFinancialDescription(value) {
      const source = String(value || "").replace(/\s+/g, " ").trim();
      if (!source) return "";

      const expanded = source.replace(/\bTRANSF\b/gi, "Transferência");
      const keepUpper = ["TED", "DOC", "CPF", "CNPJ", "MEI", "LTDA", "EIRELI", "S/A", "SA", "BR", "BRA", "CK"];
      const keepTitle = ["Pix"];
      const particles = ["a", "as", "com", "da", "das", "de", "do", "dos", "e", "em", "para", "por"];

      return expanded
        .toLowerCase()
        .split(" ")
        .map((word, index) => {
          const clean = word.replace(/[.,;:()]/g, "");
          const upper = clean.toUpperCase();
          if (keepUpper.includes(upper)) return word.replace(clean, upper);
          const titleMatch = keepTitle.find((item) => item.toLowerCase() === clean);
          if (titleMatch) return word.replace(clean, titleMatch);
          if (index > 0 && particles.includes(clean)) return word;
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(" ");
    },
    receiptUrl(item) {
      const receipt = item && (item.paymentReceipt || item.payment_receipt);
      return (receipt && (receipt.file_url || receipt.url)) || item.payment_receipt_url || "";
    },
    handleFileDrop(fieldName, event) {
      this.draggingFileField = null;
      const files = event.dataTransfer && event.dataTransfer.files;
      const file = files && files.length ? files[0] : null;
      if (!file) return;
      this.$set(this.form, fieldName, file);
    },
    fileName(file) {
      if (!file) return "";
      return file.name || String(file);
    },
    async attachPaymentReceipt(payload) {
      const file = payload.payment_receipt_file;
      delete payload.payment_receipt_file;
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name || "Comprovante de pagamento");

      const upload = await axios.post("/resources", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      payload.payment_receipt_resource_id = upload.data.content && upload.data.content.id;
      if (!payload.payment_receipt_resource_id) throw new Error("Comprovante sem recurso vinculado");
    },
    selectItems(field) {
      if (field.itemsFrom === "members") {
        return this.members.map((item) => ({
          text: this.memberLabel(item),
          value: item.id,
        }));
      }

      if (field.itemsFrom === "groups") {
        return this.groups.map((item) => ({
          text: item.name || `Núcleo ${item.id}`,
          value: item.id,
        }));
      }

      return (field.items || []).map((item) => {
        if (item && typeof item === "object") return item;
        return { text: this.optionLabel(item), value: item };
      });
    },
    memberLabel(item) {
      const user = item && item.user ? item.user : {};
      const name = user.name || item.name || item.full_name;
      const address = [item.street, item.number].filter(Boolean).join(", ");
      return [name || `Morador ${item.id}`, address].filter(Boolean).join(" - ");
    },
    queueAddressSearch(value) {
      if (this.addressSearchTimer) clearTimeout(this.addressSearchTimer);
      const query = String(value || "").trim();
      if (this.activeKey === "units") {
        this.clearValidationError("street");
        this.syncUnitAddressFromSearch(query);
      }

      if (query.length < 3) {
        this.addressSearchRequestId += 1;
        this.addressOptions = [];
        this.searchingAddress = false;
        return;
      }

      this.addressSearchTimer = setTimeout(() => this.searchAddress(query), 650);
    },
    async searchAddress(query) {
      const requestId = ++this.addressSearchRequestId;
      this.searchingAddress = true;
      try {
        const results = await this.fetchAddressCandidates(query);
        if (requestId !== this.addressSearchRequestId) return;
        const ranked = this.uniqueAddressResults(this.rankAddressResults(results, query));
        this.addressOptions = this.uniqueAddressDisplayOptions(this.enrichAddressOptions(ranked, query)).slice(0, 8);
      } catch (error) {
        if (requestId !== this.addressSearchRequestId) return;
        this.addressOptions = [];
      } finally {
        if (requestId === this.addressSearchRequestId) this.searchingAddress = false;
      }
    },
    async fetchAddressCandidates(query, limit = 8, options = {}) {
      const results = [];
      const seen = new Set();
      const searches = this.addressSearchCandidates(query);
      const context = this.addressSearchContext();

      for (const search of searches) {
        const response = await axios.get("/geocoding/search", {
          params: {
            q: search,
            limit,
            city: context.city || undefined,
            state: context.state || undefined,
            center_lat: Number.isFinite(context.centerLat) ? context.centerLat : undefined,
            center_lng: Number.isFinite(context.centerLng) ? context.centerLng : undefined,
            geocoder_only: options.geocoderOnly ? "true" : undefined,
            snap_to_road: options.snapToRoad ? "true" : undefined,
          },
        });
        const items = response.data.content || [];
        (Array.isArray(items) ? items : []).forEach((item) => {
          const key = item.place_id || `${item.lat},${item.lon}`;
          if (!seen.has(key)) {
            seen.add(key);
            results.push(item);
          }
        });
      }

      return results;
    },
    addressSearchCandidates(query) {
      const typed = this.splitAddressNumber(query);
      const restored = this.restoreCommonAddressAccents(query);
      const restoredStreet = this.restoreCommonAddressAccents(typed.street);
      const bases = [query, restored];

      if (typed.number && typed.street) {
        bases.push(`${typed.street}, ${typed.number}`);
        bases.push(`${restoredStreet}, ${typed.number}`);
      }

      return [...new Set(bases.filter(Boolean))].slice(0, 2);
    },
    addressSearchContext() {
      const group = this.groups.find((item) => Number(item.id) === Number(this.form.contribution_group_id)) || {};
      const mapCenter = this.pointMap ? this.pointMap.getCenter() : null;
      return {
        city: String(group.city || (this.association && this.association.city) || "").trim(),
        state: String(group.state || (this.association && this.association.state) || "").trim(),
        centerLat: Number(group.latitude || (this.association && this.association.center_lat) || (mapCenter && mapCenter.lat)),
        centerLng: Number(group.longitude || (this.association && this.association.center_lng) || (mapCenter && mapCenter.lng)),
      };
    },
    restoreCommonAddressAccents(value) {
      return String(value || "")
        .replace(/\babaete\b/gi, (match) => this.matchCase(match, "abaeté"));
    },
    matchCase(original, replacement) {
      if (original === original.toUpperCase()) return replacement.toUpperCase();
      if (original.charAt(0) === original.charAt(0).toUpperCase()) {
        return replacement.charAt(0).toUpperCase() + replacement.slice(1);
      }
      return replacement;
    },
    rankAddressResults(results, query) {
      const normalizedQuery = this.normalizeSearchText(query);
      const typed = this.splitAddressNumber(query);
      const normalizedStreet = this.normalizeSearchText(typed.street || query);
      const normalizedNumber = this.normalizeSearchText(typed.number);
      const context = this.addressSearchContext();
      const city = this.normalizeSearchText(context.city);
      const state = this.normalizeSearchText(context.state);

      return results.sort((a, b) => {
        return this.addressResultScore(b, normalizedQuery, normalizedStreet, normalizedNumber, city, state, context.centerLat, context.centerLng)
          - this.addressResultScore(a, normalizedQuery, normalizedStreet, normalizedNumber, city, state, context.centerLat, context.centerLng);
      });
    },
    addressResultScore(result, normalizedQuery, normalizedStreet, normalizedNumber, city, state, centerLat, centerLng) {
      const text = this.normalizeSearchText(result && result.display_name);
      const address = result && result.address ? result.address : {};
      const number = this.normalizeSearchText(address.house_number);
      const lat = Number(result && result.lat);
      const lng = Number(result && result.lon);
      let score = 0;

      if (normalizedStreet && text.includes(normalizedStreet)) score += 90;
      if (text.includes(normalizedQuery)) score += 30;
      if (normalizedNumber && number === normalizedNumber) score += 180;
      else if (normalizedNumber && text.includes(normalizedNumber)) score += 30;
      score += this.addressPrecisionScore(result);
      normalizedStreet.split(" ").filter(Boolean).forEach((part) => {
        if (text.includes(part)) score += 12;
      });
      if (city && text.includes(city)) score += 120;
      if (state && text.includes(state)) score += 40;

      if (this.isValidMapCoordinate(lat, lng) && this.isValidMapCoordinate(centerLat, centerLng)) {
        const distance = Math.sqrt(Math.pow(lat - centerLat, 2) + Math.pow(lng - centerLng, 2));
        score += Math.max(0, 80 - distance * 120);
      }

      return score;
    },
    isValidMapCoordinate(lat, lng) {
      const latitude = Number(lat);
      const longitude = Number(lng);
      return Number.isFinite(latitude)
        && Number.isFinite(longitude)
        && Math.abs(latitude) > 0.000001
        && Math.abs(longitude) > 0.000001
        && latitude >= -90
        && latitude <= 90
        && longitude >= -180
        && longitude <= 180;
    },
    normalizeSearchText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    },
    normalizeAddressSearch(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },
    splitAddressNumber(value) {
      const text = String(value || "").trim();
      const match = text.match(/^(.+?)(?:,\s*|\s+n(?:[ºo.]|umero)?\s*|\s+)(\d{1,6}[A-Za-z]?)$/i);
      if (!match) return { street: text, number: "" };
      return {
        street: match[1].trim(),
        number: match[2].trim(),
      };
    },
    addressRoad(item) {
      const address = item && item.address ? item.address : {};
      return address.road || address.pedestrian || address.residential || address.neighbourhood || (item && item.display_name) || "";
    },
    addressNumber(item, fallback = "") {
      const address = item && item.address ? item.address : {};
      return address.house_number || fallback || "";
    },
    addressNeighborhood(item) {
      const address = item && item.address ? item.address : {};
      return address.suburb || address.neighbourhood || address.quarter || address.city_district || "";
    },
    addressCity(item) {
      const address = item && item.address ? item.address : {};
      return address.city || address.town || address.village || address.municipality || "";
    },
    normalizedAddressLabel(street, number) {
      return [street, number].filter(Boolean).join(", ");
    },
    autocompleteIntegrationId(item) {
      if (!item) return "";
      if (item.place_id) return String(item.place_id);
      if (item.osm_type && item.osm_id) return `${item.osm_type}:${item.osm_id}`;
      return item.osm_id ? String(item.osm_id) : "";
    },
    uniqueAddressResults(results) {
      const seen = new Set();
      return results.filter((item) => {
        const address = item.address || {};
        const key = [
          this.addressRoad(item),
          address.suburb || address.neighbourhood || address.quarter || address.city_district,
          address.city || address.town || address.village || address.municipality,
          address.state,
        ].map((part) => this.normalizeSearchText(part)).filter(Boolean).join("|");
        const fallback = this.normalizeSearchText(String(item.display_name || "").replace(/,?\s*\d{5}-?\d{3}(?=,|$)/g, ""));
        const dedupeKey = key || fallback || (item.place_id || `${item.lat},${item.lon}`);
        if (seen.has(dedupeKey)) return false;
        seen.add(dedupeKey);
        return true;
      });
    },
    uniqueAddressDisplayOptions(results) {
      const seen = new Set();
      return results.filter((item) => {
        const key = this.normalizeSearchText(item && item.display_name);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    cleanAddressDisplayName(value) {
      return String(value || "")
        .replace(/,?\s*\d{5}-?\d{3}(?=,|$)/g, "")
        .replace(/\s+,/g, ",")
        .replace(/,\s*,/g, ",")
        .trim();
    },
    enrichAddressOptions(results, query) {
      const typedAddress = this.splitAddressNumber(query);
      return results.map((item) => {
        const address = item.address || {};
        const road = this.addressRoad(item);
        const number = address.house_number || typedAddress.number;
        const baseItem = Object.assign({}, item, {
          display_name: this.cleanAddressDisplayName(item.display_name),
          postcodeReliable: !!address.house_number,
        });
        if (!number || address.house_number || !road) return baseItem;

        const displayName = String(baseItem.display_name || "");
        const normalizedDisplay = this.normalizeAddressSearch(displayName).toLowerCase();
        const normalizedCandidate = this.normalizeAddressSearch(`${road}, ${number}`).toLowerCase();
        if (normalizedDisplay.includes(normalizedCandidate)) return baseItem;

        return Object.assign({}, baseItem, {
          display_name: displayName.replace(road, `${road}, ${number}`),
        });
      });
    },
    syncUnitAddressFromSearch(value) {
      const parsed = this.splitAddressNumber(value);
      if (parsed.number) {
        this.$set(this.form, "number", parsed.number);
        return parsed;
      }
      return parsed;
    },
    applyAddressSuggestion(item) {
      if (!item || !item.address) return;
      const address = item.address || {};
      const road = this.addressRoad(item);
      const typedAddress = this.splitAddressNumber(this.addressSearch);
      const number = this.addressNumber(item, typedAddress.number);
      const normalizedAddress = this.normalizedAddressLabel(road, number);

      if (this.activeKey === "points" || this.isBusinessDialog) {
        const pointAddress = this.pointAddressLabel(item, road, number);
        this.$set(this.form, "address", pointAddress);
        this.addressSearch = pointAddress;
        this.addressLocationPrecision = item.geocode_precision || item.location_type || "";
        if (item.lat && item.lon) this.setPointLocation(item.lat, item.lon, true);
        return;
      }

      this.$set(this.form, "street", road);
      if (number) this.$set(this.form, "number", number);
      this.$set(this.form, "address_normalized", normalizedAddress || road);
      this.$set(this.form, "neighborhood", this.addressNeighborhood(item));
      this.$set(this.form, "city", this.addressCity(item));
      this.$set(this.form, "state", this.stateToUf(address.state));
      this.$set(this.form, "address_integration_id", this.autocompleteIntegrationId(item));
      this.$set(this.form, "address_integration_source", item.source || "nominatim");
      if (item.lat && item.lon) {
        this.addressLocationPrecision = item.geocode_precision || item.location_type || "";
        this.setPointLocation(item.lat, item.lon, true);
      }
      this.addressSearch = normalizedAddress || road;
      this.fillPostalCodeFromViaCep();
    },
    pointAddressLabel(item, road, number) {
      const address = item && item.address ? item.address : {};
      const base = [road, number].filter(Boolean).join(", ");
      const details = [
        this.addressNeighborhood(item),
        this.addressCity(item),
        this.stateToUf(address.state),
      ].filter(Boolean);

      return [base || this.cleanAddressDisplayName(item && item.display_name), ...details]
        .filter(Boolean)
        .join(", ");
    },
    async fillPostalCodeFromViaCep() {
      const state = String(this.form.state || "").trim();
      const city = String(this.form.city || "").trim();
      const street = String(this.form.street || "").trim();
      if (!state || !city || !street) return;

      try {
        const url = `https://viacep.com.br/ws/${encodeURIComponent(state)}/${encodeURIComponent(city)}/${encodeURIComponent(street)}/json/`;
        const response = await fetch(url, { headers: { Accept: "application/json" } });
        const results = await response.json();
        const item = Array.isArray(results) ? results[0] : null;
        if (item && item.cep) this.$set(this.form, "zip_code", String(item.cep).replace(/\D/g, ""));
        if (item && item.bairro && !this.form.neighborhood) this.$set(this.form, "neighborhood", item.bairro);
        if (item && item.localidade && !this.form.city) this.$set(this.form, "city", item.localidade);
        if (item && item.uf && !this.form.state) this.$set(this.form, "state", item.uf);
      } catch (error) {
        // CEP é complementar; não bloqueia o cadastro.
      }
    },
    stateToUf(state) {
      const states = {
        acre: "AC",
        alagoas: "AL",
        amapa: "AP",
        amazonas: "AM",
        bahia: "BA",
        ceara: "CE",
        "distrito federal": "DF",
        "espirito santo": "ES",
        goias: "GO",
        maranhao: "MA",
        "mato grosso": "MT",
        "mato grosso do sul": "MS",
        "minas gerais": "MG",
        para: "PA",
        paraiba: "PB",
        parana: "PR",
        pernambuco: "PE",
        piaui: "PI",
        "rio de janeiro": "RJ",
        "rio grande do norte": "RN",
        "rio grande do sul": "RS",
        rondonia: "RO",
        roraima: "RR",
        "santa catarina": "SC",
        "sao paulo": "SP",
        sergipe: "SE",
        tocantins: "TO",
      };
      const key = this.normalizeSearchText(state);
      return states[key] || String(state || "").trim().toUpperCase().slice(0, 2);
    },
    optionValue(item) {
      return item && typeof item === "object" ? item.value : item;
    },
    optionLabel(value) {
      if (value === undefined || value === null || value === "") return "-";
      if (typeof value === "boolean") return value ? "Sim" : "Não";
      const raw = String(value).trim();
      const lowerKey = raw.toLowerCase();
      const normalizedKey = this.normalizeSearchText(raw).replace(/\s+/g, "_");
      return this.optionLabels[raw] || this.optionLabels[lowerKey] || this.optionLabels[normalizedKey] || raw;
    },
    reportSummary(item) {
      try {
        const metrics = JSON.parse(item.metrics_json || "{}");
        const financial = metrics.financial || {};
        return `${metrics.members || 0} moradores, ${metrics.demands_open || 0} chamados, saldo ${this.money(financial.final_balance)}`;
      } catch (error) {
        return "-";
      }
    },
    notifySuccess(message) {
      this.$dialog && this.$dialog.notify && this.$dialog.notify.success(message, { position: "top-right", timeout: 3000 });
    },
    notifyError(message) {
      this.$dialog && this.$dialog.notify && this.$dialog.notify.error(message, { position: "top-right", timeout: 4000 });
    },
  },
};
</script>

<style scoped lang="scss">
.resource-toolbar,
.panel-heading,
.toolbar-actions {
  display: flex;
  align-items: center;
}

.resource-toolbar {
  margin-bottom: 14px;
  justify-content: space-between;
  gap: 18px;

  h2,
  p {
    margin: 0;
  }

  h2 {
    color: #111827;
    font-size: 20px;
  }

  p {
    margin-top: 3px;
    color: #66736b;
    font-size: 13px;
  }
}

.toolbar-actions {
  gap: 10px;
}

.toolbar-create-button {
  min-height: 32px;
  padding: 0 14px !important;
  color: #ffffff !important;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.08em;

  .v-icon {
    color: currentColor !important;
  }
}

.table-panel {
  padding: 18px;
  background: #ffffff;
  border: 1px solid #e1e7e0;
  border-radius: 8px;
}

.table-panel::v-deep {
  th:last-child,
  td:last-child {
    min-width: 96px;
    position: sticky;
    right: 0;
    z-index: 2;
    background: #ffffff;
    box-shadow: -10px 0 16px rgba(17, 24, 39, 0.04);
    white-space: nowrap;
  }

  th:last-child {
    z-index: 3;
  }
}

.mobile-card-list {
  display: none;
}

.panel-heading {
  margin-bottom: 14px;
  justify-content: space-between;
  gap: 16px;

  h3,
  p {
    margin: 0;
  }

  h3 {
    color: #111827;
    font-size: 18px;
  }

  p {
    margin-top: 3px;
    color: #66736b;
    font-size: 13px;
  }
}

.empty-state {
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;

  strong {
    color: #111827;
  }

  span {
    color: #66736b;
    font-size: 13px;
  }
}

.responsible-cell {
  display: grid;
  gap: 3px;
  line-height: 1.2;

  strong {
    color: #10223f;
    font-size: 13px;
    font-weight: 400;
    text-transform: none;
  }

  span {
    color: #66736b;
    font-size: 11px;
    font-weight: 400;
    text-transform: none;
  }
}

.vote-title {
  margin: 0 0 10px;
  color: #111827;
  font-weight: 800;
}

.delete-dialog {
  .v-card__title {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 22px 24px 8px;

    strong {
      display: block;
      color: #111827;
      font-size: 20px;
      font-weight: 800;
      line-height: 1.2;
    }

    span {
      display: block;
      margin-top: 4px;
      color: #66736b;
      font-size: 13px;
      line-height: 1.4;
    }
  }

  .v-card__text {
    padding: 12px 24px 6px;
    color: #374151;
    font-size: 14px;
    line-height: 1.5;
  }

  .v-card__actions {
    padding: 14px 24px 22px;
  }
}

.form-dialog {
  max-height: calc(100vh - 72px);
  display: flex;
  flex-direction: column;

  .v-card__title {
    flex: 0 0 auto;
  }

  .form-dialog-body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding-bottom: 16px;
  }

  .v-card__actions {
    position: sticky;
    bottom: 0;
    z-index: 2;
    flex: 0 0 auto;
    padding: 14px 24px;
    border-top: 1px solid #e4ebe6;
    background: #ffffff;
  }
}

.business-dialog-fields {
  margin: -6px;

  > .col,
  > [class*="col-"] {
    padding: 6px;
  }
}

.business-step-dialog {
  display: grid;
  gap: 12px;
}

.business-step-heading {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #057a3d;
  background: #f6faf7;
  border: 1px solid #dce6df;
  border-radius: 8px;

  .v-icon {
    color: currentColor;
  }

  strong,
  span {
    display: block;
  }

  strong {
    color: #10223f;
    font-size: 13px;
    font-weight: 900;
  }

  span {
    margin-top: 2px;
    color: #66736b;
    font-size: 12px;
    line-height: 1.35;
  }
}

.business-step-panel {
  min-height: 0;
}

.business-map-picker {
  background: #ffffff;
}

.business-map {
  height: 300px;
}

.unit-dialog-layout {
  display: grid;
  gap: 18px;
}

.unit-dialog-main {
  min-width: 0;
}

.schedule-list-field {
  padding: 12px;
  border: 1px solid #dce6df;
  border-radius: 8px;
  background: #f8faf8;
}

.receipt-dropzone {
  padding: 10px;
  border: 1px dashed #b7c9be;
  border-radius: 8px;
  background: #f8faf8;
  transition: border-color 0.15s ease, background 0.15s ease;

  &.dragging {
    border-color: #007f3d;
    background: #edf7f1;
  }

  &.filled {
    border-style: solid;
  }
}

.unit-analytics {
  margin-top: 4px;
}

.unit-analytics-grid,
.unit-analytics-lists {
  display: grid;
  gap: 10px;
}

.unit-analytics-grid {
  margin-top: 12px;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
}

.unit-analytics-card,
.unit-analytics-lists > div {
  padding: 12px;
  border: 1px solid #dce6df;
  border-radius: 8px;
  background: #f8faf8;
}

.unit-analytics-card {
  min-height: 88px;

  span,
  small {
    display: block;
    color: #66736b;
    font-size: 12px;
    line-height: 1.35;
  }

  strong {
    display: block;
    margin-top: 6px;
    color: #111827;
    font-size: 18px;
    font-weight: 900;
    line-height: 1.15;
  }

  &.danger strong {
    color: #a12828;
  }
}

.unit-analytics-lists {
  margin-top: 10px;
  grid-template-columns: 1fr;

  strong {
    display: block;
    margin-bottom: 6px;
    color: #111827;
    font-size: 13px;
    font-weight: 900;
  }

  small {
    color: #66736b;
    font-size: 12px;
  }
}

.financial-history-panel {
  min-height: 128px;
}

.analytics-row {
  padding: 12px 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  border-top: 1px solid #e4ebe6;

  span,
  b {
    min-width: 0;
    color: #1f2937;
    font-size: 12px;
    line-height: 1.35;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  b {
    flex: 0 0 auto;
    color: #66736b;
    font-weight: 800;
    text-align: right;
  }
}

.dropzone-hint {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #66736b;
  font-size: 12px;

  .v-icon {
    color: #007f3d;
  }
}

.form-section-heading {
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid #e1e7e0;

  strong,
  span {
    display: block;
  }

  strong {
    color: #111827;
    font-size: 15px;
    font-weight: 900;
  }

  span {
    margin-top: 4px;
    color: #66736b;
    font-size: 13px;
    line-height: 1.4;
  }

  &.compact {
    margin-top: 4px;
    padding-top: 14px;
  }
}

.form-inline-alert {
  padding: 12px 14px;
  display: flex;
  gap: 10px;
  color: #1f3b2d;
  background: #edf7f1;
  border: 1px solid #cce6d6;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.4;
}

.schedule-list-heading {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  span {
    color: #111827;
    font-size: 13px;
    font-weight: 900;
  }
}

.schedule-row + .schedule-row {
  margin-top: 6px;
}

.schedule-remove {
  display: flex;
  align-items: center;
  justify-content: center;
}

.point-location-step {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.point-step-heading {
  padding: 12px 14px;
  border: 1px solid #dce6df;
  border-radius: 8px;
  background: #f8faf8;

  strong,
  span {
    display: block;
  }

  strong {
    color: #111827;
    font-size: 14px;
    font-weight: 900;
  }

  span {
    margin-top: 3px;
    color: #66736b;
    font-size: 12px;
  }
}

.point-map-picker {
  overflow: hidden;
  border: 1px solid #dce6df;
  border-radius: 8px;
  background: #f8faf8;
}

.unit-address-map {
  margin-top: 2px;
}

.point-map-toolbar,
.point-map-footer {
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.point-map-toolbar {
  border-bottom: 1px solid #e5ece7;

  strong,
  span {
    display: block;
  }

  strong {
    color: #111827;
    font-size: 14px;
    font-weight: 900;
  }

  span {
    margin-top: 2px;
    color: #66736b;
    font-size: 12px;
  }
}

.point-map {
  height: 280px;
  width: 100%;
  background: #edf2ef;
}

.point-map::v-deep .leaflet-control-attribution {
  padding: 1px 4px;
  color: #7b8792;
  background: rgba(255, 255, 255, 0.58);
  font-size: 9px;
  line-height: 1.2;
  opacity: 0.72;

  a {
    color: inherit;
  }
}

::v-deep .point-location-marker {
  span {
    width: 34px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    background: #087f3d;
    border: 3px solid #ffffff;
    border-radius: 50% 50% 50% 10px;
    box-shadow: 0 10px 22px rgba(8, 127, 61, 0.28);
    transform: rotate(-45deg);
  }

  i {
    font-size: 18px;
    line-height: 1;
    transform: rotate(45deg);
  }
}

.point-map-footer {
  min-height: 40px;
  border-top: 1px solid #e5ece7;
  color: #267062;
  font-size: 12px;
  font-weight: 800;
}

@media (max-width: 780px) {
  .resource-toolbar,
  .panel-heading,
  .point-map-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .resource-toolbar {
    gap: 12px;
  }

  .resource-toolbar h2 {
    font-size: 21px;
    line-height: 1.2;
  }

  .resource-toolbar p {
    font-size: 14px;
    line-height: 1.45;
  }

  .toolbar-actions {
    flex-direction: row;
    justify-content: flex-end;
  }

  .toolbar-actions .v-btn {
    min-width: auto;
    padding: 0 10px !important;
  }

  .table-panel {
    display: none !important;
  }

  .mobile-card-list {
    display: grid;
    gap: 12px;
  }

  .mobile-data-card,
  .mobile-list-state {
    padding: 16px;
    background: #ffffff;
    border: 1px solid #e1e7e0;
    border-radius: 14px;
    box-shadow: 0 10px 26px rgba(16, 34, 63, 0.05);
  }

  .mobile-list-state {
    min-height: 140px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #66736b;
    text-align: center;

    strong {
      color: #10223f;
    }
  }

  .mobile-data-card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;

    strong,
    span {
      display: block;
    }

    strong {
      color: #10223f;
      font-size: 16px;
      font-weight: 900;
      line-height: 1.25;
    }

    span {
      margin-top: 3px;
      color: #66736b;
      font-size: 13px;
      line-height: 1.35;
    }

    .v-chip {
      flex: 0 0 auto;
    }
  }

  .mobile-data-card-body {
    margin-top: 14px;
    display: grid;
    gap: 10px;
  }

  .mobile-data-field {
    padding-top: 10px;
    display: grid;
    gap: 4px;
    border-top: 1px solid #eef2ef;

    span {
      color: #66736b;
      font-size: 11px;
      font-weight: 800;
      line-height: 1.2;
      text-transform: uppercase;
    }

    strong {
      color: #263548;
      font-size: 14px;
      font-weight: 700;
      line-height: 1.35;
      overflow-wrap: anywhere;
    }
  }

  .mobile-data-card-actions {
    margin-top: 12px;
    padding-top: 10px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    border-top: 1px solid #eef2ef;
  }

  .point-map {
    height: 260px;
  }

  .unit-analytics-grid,
  .unit-analytics-lists {
    grid-template-columns: 1fr;
  }
}
</style>
